/**
 * Store Reviews Section - Okendo Integration
 * Implements store-wide reviews (not product-specific) with dynamic data fetching from Okendo API
 */

class StoreReviewsSection {
  #okendoUserId;
  #abortController;
  #reviewsData;
  #currentPage = 1;
  #itemsPerPage = 30;
  #currentOrderBy = 'date desc';
  #hasPhotoFilter = false;
  #allReviews = [];
  #nextUrl = null;
  #isLoadingMore = false;
  #allMediaItems = [];
  #mediaNextUrl = null;
  #isLoadingMoreMedia = false;
  #renderedMediaCount = 0;
  #renderedModalCardsCount = 0;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #reviewsUiRefreshTimer = null;
  #mediaBackgroundRunning = false;
  /** @type {string|null} */
  #lastListFlushSignature = null;
  /** @type {string|null} */
  #lastGalleryFlushSignature = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #gallerySliderLayoutTimer = null;
  /** @type {Map<string, object>} */
  #reviewDetailsById = new Map();
  /** @type {Set<string>} */
  #reviewFetchInFlight = new Set();
  /** @type {Set<string>} */
  #reviewFetchSettled = new Set();
  /** @type {number} */
  #lastPaginationFilteredCount = -1;
  /** @type {{ byVariantId: Record<string, object>, byProductId: Record<string, object> }} */
  #variantLabelsById = { byVariantId: {}, byProductId: {} };
  /** @type {Map<string, object|null>} */
  #productJsonByHandle = new Map();
  /** @type {Map<string, Promise<object|null>>} */
  #pendingVariantLookups = new Map();

  constructor() {
    this.#okendoUserId = this.#getOkendoUserId();
    this.#abortController = new AbortController();
    this.#reviewsData = {
      media: null,
      reviews: null,
      aggregate: null
    };
    this.#init();
  }

  #init() {
    this.#setupEventListeners();
    this.#loadReviewsData();
  }

  #getOkendoUserId() {
    // Try to get Okendo user ID from various sources
    return window.Shopify?.shop?.metafields?.okendo?.userId || 
           window.okendo?.userId || 
           document.querySelector('meta[name="okendo-user-id"]')?.content || '';
  }

  #setupEventListeners() {
    const orderBySelect = document.getElementById('order-by-select');
    const hasPhotoFilter = document.getElementById('has-photo-filter');

    if (orderBySelect) {
      orderBySelect.addEventListener('change', () => this.#handleOrderByChange());
    }

    if (hasPhotoFilter) {
      this.#hasPhotoFilter = hasPhotoFilter.checked;
      hasPhotoFilter.addEventListener('change', () => this.#handlePhotoFilterChange());
    }
  }

  /** Keep in-memory flag aligned with the checkbox (source of truth in DOM). */
  #syncHasPhotoFilterFromDom() {
    const el = document.getElementById('has-photo-filter');
    if (!el) return;
    this.#hasPhotoFilter = el.checked;
  }

  async #loadReviewsData() {
    try {
      this.#showLoadingState();
      
      if (!this.#okendoUserId) {
        const errorMsg = 'Okendo user ID not found. Check: window.Shopify.shop.metafields.okendo.userId, window.okendo.userId, or meta[name="okendo-user-id"]';
        console.error('🔍 [OKENDO-STORE-REVIEWS] Error:', errorMsg);
        this.#showError('Configuration Error', errorMsg);
        this.#hideLoadingState();
        return;
      }
      
      // Fetch initial data - reviews limited to 25 for immediate render
      const [mediaData, reviewsData, aggregateData] = await Promise.all([
        this.#fetchReviewsMedia(),
        this.#fetchReviews(25, this.#currentOrderBy),
        this.#fetchReviewsAggregate()
      ]);

      // Store initial reviews and nextUrl
      this.#allReviews = reviewsData?.reviews || [];
      this.#nextUrl = reviewsData?.nextUrl || null;
      this.#mediaNextUrl = mediaData?.nextUrl || null;
      
      // Store media items for gallery
      this.#allMediaItems = this.#normalizeOkendoMediaList(mediaData?.media || []);
      this.#mergeReviewMediaIntoAllReviews();

      this.#reviewsData = {
        media: mediaData,
        reviews: reviewsData,
        aggregate: aggregateData
      };

      // Render immediately with initial 25 reviews
      this.#populateAllSections();
      this.#captureFlushSignaturesBaseline();
      this.#hideLoadingState();
      
      // Start background fetching of remaining reviews
      // Use setTimeout to ensure it runs after render is complete
      // Paginate `review_media` immediately — gallery slides are driven by `#allMediaItems`, not by how
      // many `/reviews` pages have loaded (merging media onto reviews used to hide most tiles for minutes).
      if (this.#mediaNextUrl) {
        queueMicrotask(() => {
          this.#fetchAllMediaInBackground().catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('🔍 [OKENDO-STORE-REVIEWS] Background media fetch failed:', error);
            }
          });
        });
      }
      if (this.#nextUrl) {
        setTimeout(() => {
          this.#fetchAllReviewsInBackground().catch(error => {
            console.error('🔍 [OKENDO-STORE-REVIEWS] Background fetch failed:', error);
          });
        }, 120);
      }
    } catch (error) {
      console.error('🔍 [OKENDO-STORE-REVIEWS] Error:', error);
      const errorType = error.name === 'AbortError' ? 'Request Cancelled' : 'API Error';
      const errorDetails = `${error.message}\n\nOkendo User ID: ${this.#okendoUserId || 'Not found'}`;
      this.#showError(errorType, errorDetails);
      this.#hideLoadingState();
    }
  }

  /**
   * Okendo returns `nextUrl` as a path or full URL. Pagination paths are often `/stores/{id}/reviews?...`
   * (no `/v1/`). Hitting `https://api.okendo.io/stores/...` triggers CORS/preflight failures in the browser;
   * the documented API base is `https://api.okendo.io/v1/...`.
   * @param {string} nextPathOrUrl
   * @returns {string}
   */
  #resolveOkendoNextUrl(nextPathOrUrl) {
    if (!nextPathOrUrl || typeof nextPathOrUrl !== 'string') return '';
    const t = nextPathOrUrl.trim();
    const origin = 'https://api.okendo.io';

    if (t.startsWith('http://') || t.startsWith('https://')) {
      try {
        const u = new URL(t);
        if (u.hostname === 'api.okendo.io') {
          const p = u.pathname || '';
          if (p !== '/v1' && !p.startsWith('/v1/')) {
            u.pathname = '/v1' + (p.startsWith('/') ? p : `/${p}`);
          }
        }
        return u.toString();
      } catch {
        return t;
      }
    }

    let path = t.startsWith('/') ? t : `/${t}`;
    if (path !== '/v1' && !path.startsWith('/v1/')) {
      path = `/v1${path}`;
    }
    return `${origin}${path}`;
  }

  #mediaDedupeKey(mediaItem) {
    if (!mediaItem || typeof mediaItem !== 'object') return '';
    return String(
      mediaItem.streamId ??
        mediaItem.id ??
        mediaItem.mediaId ??
        mediaItem.dynamicImageKey ??
        mediaItem.subscriberMediaId ??
        mediaItem.largeUrl ??
        mediaItem.fullSizeUrl ??
        mediaItem.thumbnailUrl ??
        mediaItem.imageUrls?.largeUrl ??
        mediaItem.videoUrls?.fullSizeUrl ??
        ''
    );
  }

  /**
   * Okendo `/review_media` nests files under `videoUrls` / `imageUrls`.
   * `/reviews` flattens the same fields onto the media item.
   * @param {object|null|undefined} mediaItem
   * @returns {boolean}
   */
  #isVideoMedia(mediaItem) {
    if (!mediaItem || typeof mediaItem !== 'object') return false;
    return (
      mediaItem.type === 'video' ||
      mediaItem.mediaType === 'video' ||
      Boolean(mediaItem.videoUrls) ||
      Boolean(mediaItem.videoUrl) ||
      Boolean(mediaItem.video_url)
    );
  }

  /**
   * @param {unknown} url
   * @returns {boolean}
   */
  #looksLikeVideoUrl(url) {
    return typeof url === 'string' && /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
  }

  /**
   * @param {object|null|undefined} mediaItem
   * @returns {string}
   */
  #getVideoSrc(mediaItem) {
    if (!mediaItem) return '';
    const urls = mediaItem.videoUrls && typeof mediaItem.videoUrls === 'object' ? mediaItem.videoUrls : {};
    const mp4First = [
      mediaItem.videoUrl,
      mediaItem.video_url,
      urls.fullSizeUrl,
      mediaItem.fullSizeUrl,
      mediaItem.largeUrl
    ];
    for (const candidate of mp4First) {
      if (typeof candidate === 'string' && /\.mp4(\?|#|$)/i.test(candidate)) return candidate;
    }
    const fallbacks = [...mp4First, mediaItem.fhdWebmUrl, urls.fhdWebmUrl];
    for (const candidate of fallbacks) {
      if (this.#looksLikeVideoUrl(candidate)) return candidate;
    }
    return '';
  }

  /**
   * @param {object|null|undefined} mediaItem
   * @returns {string}
   */
  #getVideoPoster(mediaItem) {
    if (!mediaItem) return '';
    const urls = mediaItem.videoUrls && typeof mediaItem.videoUrls === 'object' ? mediaItem.videoUrls : {};
    return (
      mediaItem.videoPosterUrl ||
      urls.videoPosterUrl ||
      mediaItem.thumbnailUrl ||
      urls.thumbnailUrl ||
      ''
    );
  }

  /**
   * @param {object|null|undefined} mediaItem
   * @returns {string}
   */
  #getVideoWebm(mediaItem) {
    if (!mediaItem) return '';
    const urls = mediaItem.videoUrls && typeof mediaItem.videoUrls === 'object' ? mediaItem.videoUrls : {};
    const candidate = mediaItem.fhdWebmUrl || urls.fhdWebmUrl || '';
    return typeof candidate === 'string' && /\.webm(\?|#|$)/i.test(candidate) ? candidate : '';
  }

  /**
   * Native review video. `preload="none"` avoids the spinner loop: Okendo mp4s keep
   * the moov atom at the end, so `preload="metadata"` downloads most of the file,
   * and list re-renders were restarting that load on every background fetch.
   * @param {object} options
   * @param {string} options.src
   * @param {string} [options.webm]
   * @param {string} [options.poster]
   * @param {string} [options.className]
   * @returns {string}
   */
  #buildReviewVideoHtml({ src, webm = '', poster = '', className = '' }) {
    if (!src) return '';
    const posterAttr = poster ? ` poster="${poster}"` : '';
    const webmSource = webm ? `<source src="${webm}" type="video/webm">` : '';
    return `
      <video
        controls
        playsinline
        preload="none"
        class="${className}"
        ${posterAttr}
        data-review-video-src="${src}"
      >
        <source src="${src}" type="video/mp4">
        ${webmSource}
      </video>
    `;
  }

  /**
   * Keep already-loaded video nodes across innerHTML refreshes so play/buffering is not reset.
   * @param {HTMLElement} root
   * @param {string} html
   */
  #paintHtmlReusingVideos(root, html) {
    if (!root) return;
    const previous = new Map();
    root.querySelectorAll('video[data-review-video-src]').forEach((video) => {
      const key = video.getAttribute('data-review-video-src');
      if (key && !previous.has(key)) previous.set(key, video);
    });
    const sliderScroll = new Map();
    root.querySelectorAll('electric-slider[data-id]').forEach((el) => {
      const id = el.getAttribute('data-id');
      const feed = el.querySelector('.electric-slider__feed');
      if (id && feed) sliderScroll.set(id, feed.scrollLeft);
    });
    root.innerHTML = html;
    root.querySelectorAll('video[data-review-video-src]').forEach((video) => {
      const key = video.getAttribute('data-review-video-src');
      const kept = previous.get(key);
      if (kept && kept !== video) video.replaceWith(kept);
    });
    if (sliderScroll.size === 0) return;
    requestAnimationFrame(() => {
      root.querySelectorAll('electric-slider[data-id]').forEach((el) => {
        const id = el.getAttribute('data-id');
        const feed = el.querySelector('.electric-slider__feed');
        const left = sliderScroll.get(id) || 0;
        if (!feed || left <= 0) return;
        feed.style.scrollSnapType = 'none';
        feed.scrollLeft = left;
        requestAnimationFrame(() => {
          feed.style.scrollSnapType = '';
        });
      });
    });
  }

  /**
   * Lift nested Okendo URLs so gallery, modal, and list share one shape.
   * @param {object|null|undefined} mediaItem
   * @returns {object|null|undefined}
   */
  #normalizeOkendoMediaItem(mediaItem) {
    if (!mediaItem || typeof mediaItem !== 'object') return mediaItem;
    const videoSrc = this.#getVideoSrc(mediaItem);
    const poster = this.#getVideoPoster(mediaItem);
    if (this.#isVideoMedia(mediaItem) && videoSrc) {
      mediaItem.type = mediaItem.type || 'video';
      mediaItem.fullSizeUrl = videoSrc;
      mediaItem.videoUrl = mediaItem.videoUrl || videoSrc;
      if (poster) {
        mediaItem.videoPosterUrl = mediaItem.videoPosterUrl || poster;
        mediaItem.thumbnailUrl = mediaItem.thumbnailUrl || poster;
      }
    }
    const imageUrls = mediaItem.imageUrls;
    if (imageUrls && typeof imageUrls === 'object') {
      if (!mediaItem.fullSizeUrl) mediaItem.fullSizeUrl = imageUrls.fullSizeUrl || '';
      if (!mediaItem.largeUrl) mediaItem.largeUrl = imageUrls.largeUrl || '';
      if (!mediaItem.thumbnailUrl) mediaItem.thumbnailUrl = imageUrls.thumbnailUrl || '';
    }
    return mediaItem;
  }

  /**
   * @param {unknown} items
   * @returns {object[]}
   */
  #normalizeOkendoMediaList(items) {
    if (!Array.isArray(items)) return [];
    return items.map((item) => this.#normalizeOkendoMediaItem(item));
  }

  /**
   * Single canonical key for list rows / merge (first non-empty id on the review).
   * Do not use `subscriber.subscriberId` — that is the store/subscriber id, not a review row id.
   */
  #reviewRowMatchKey(review) {
    if (!review) return '';
    const fetchId =
      review._okendoReviewFetchId ||
      (review.primaryMedia ? this.#getMediaReviewFetchId(review.primaryMedia) : '');
    if (this.#hasPhotoFilter && fetchId) {
      return this.#normalizeLinkId(fetchId);
    }
    if (!this.#hasPhotoFilter && review.primaryMedia) {
      const mediaKey = this.#mediaDedupeKey(review.primaryMedia);
      if (mediaKey) return mediaKey;
    }
    const raw =
      review.reviewId ??
      review.id ??
      review.review_id ??
      review.subscriberReviewId ??
      review.uuid;
    return raw === undefined || raw === null ? '' : this.#normalizeLinkId(raw);
  }

  /**
   * Raw review UUID from `review_media` for `/reviews/{id}` fetches (preserve API casing).
   * @param {object|null|undefined} mediaItem
   * @returns {string}
   */
  #getMediaReviewFetchId(mediaItem) {
    if (!mediaItem || typeof mediaItem !== 'object') return '';
    const nested = mediaItem.review && typeof mediaItem.review === 'object' ? mediaItem.review : null;
    const raw =
      mediaItem.reviewId ??
      mediaItem.review_id ??
      mediaItem.reviewUuid ??
      mediaItem.review_uuid ??
      mediaItem.subscriberReviewId ??
      mediaItem.subscriberReviewID ??
      nested?.reviewId ??
      nested?.id ??
      nested?.review_id ??
      nested?.uuid;
    return raw === undefined || raw === null ? '' : String(raw).trim();
  }

  /**
   * @param {...unknown} candidates
   * @returns {string[]}
   */
  /**
   * Normalize Okendo ids for Map lookups (UUID casing differs between review_media and /reviews).
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeLinkId(value) {
    if (value === undefined || value === null) return '';
    return String(value).trim().toLowerCase();
  }

  #collectLinkIds(...candidates) {
    const out = [];
    const seen = new Set();
    for (const c of candidates) {
      const s = this.#normalizeLinkId(c);
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }

  /**
   * All ids Okendo may use to tie a `/reviews` row to `review_media` (often different fields per payload).
   * @param {object|null|undefined} review
   * @returns {string[]}
   */
  #reviewAllLinkIds(review) {
    if (!review || typeof review !== 'object') return [];
    return this.#collectLinkIds(
      review.reviewId,
      review.id,
      review.review_id,
      review.reviewUuid,
      review.review_uuid,
      review.subscriberReviewId,
      review.subscriberReviewID,
      review.uuid
    );
  }

  /**
   * @param {object|null|undefined} mediaItem
   * @returns {string[]}
   */
  #mediaItemAllLinkIds(mediaItem) {
    if (!mediaItem || typeof mediaItem !== 'object') return [];
    const n = mediaItem.review;
    const nestedId = typeof n === 'string' ? n : null;
    const nestedObj = n && typeof n === 'object' ? n : null;
    return this.#collectLinkIds(
      mediaItem.reviewId,
      mediaItem.review_id,
      mediaItem.reviewUuid,
      mediaItem.review_uuid,
      mediaItem.subscriberReviewId,
      mediaItem.subscriberReviewID,
      nestedId,
      nestedObj?.id,
      nestedObj?.reviewId,
      nestedObj?.review_id,
      nestedObj?.reviewUuid,
      nestedObj?.review_uuid,
      nestedObj?.subscriberReviewId,
      nestedObj?.subscriberReviewID,
      nestedObj?.uuid
    );
  }

  /**
   * @param {object} mediaItem
   * @param {Map<string, object>} reviewByKey
   * @param {Map<string, object>} [mediaKeyToReview]
   * @returns {object|null}
   */
  /**
   * Match media → review only via shared review ids (never via loose media-key merge).
   * @param {object} mediaItem
   * @param {Map<string, object>} reviewByKey
   * @returns {object|null}
   */
  #findReviewForMediaItemByReviewId(mediaItem, reviewByKey) {
    for (const id of this.#mediaItemAllLinkIds(mediaItem)) {
      const hit = reviewByKey.get(id);
      if (hit) return hit;
      const cached = this.#reviewDetailsById.get(id);
      if (cached) return cached;
    }
    return null;
  }

  #findLoadedReviewForMediaItem(mediaItem, reviewByKey, mediaKeyToReview) {
    const byReviewId = this.#findReviewForMediaItemByReviewId(mediaItem, reviewByKey);
    if (byReviewId) return byReviewId;

    const mediaReviewIds = this.#mediaItemAllLinkIds(mediaItem);
    if (mediaReviewIds.length > 0) return null;

    const dk = this.#mediaDedupeKey(mediaItem);
    if (dk && mediaKeyToReview?.has(dk)) {
      return mediaKeyToReview.get(dk);
    }
    return null;
  }

  #buildReviewByKeyMap() {
    const reviewByKey = new Map();
    for (const r of this.#allReviews) {
      for (const k of this.#reviewAllLinkIds(r)) {
        if (!reviewByKey.has(k)) reviewByKey.set(k, r);
      }
    }
    return reviewByKey;
  }

  #buildMediaKeyToReviewMap() {
    const map = new Map();
    for (const r of this.#allReviews) {
      const linkIds = new Set(this.#reviewAllLinkIds(r));
      if (!Array.isArray(r.media)) continue;
      for (const m of r.media) {
        const mediaReviewIds = this.#mediaItemAllLinkIds(m);
        if (mediaReviewIds.length > 0 && !mediaReviewIds.some((id) => linkIds.has(id))) {
          continue;
        }
        const k = this.#mediaDedupeKey(m);
        if (k && !map.has(k)) map.set(k, r);
      }
    }
    return map;
  }

  /** True when review + review_media background pagination has finished. */
  #isGalleryFetchSettled() {
    return !this.#nextUrl && !this.#isLoadingMore && !this.#mediaNextUrl && !this.#mediaBackgroundRunning;
  }

  /** True when `/reviews` background pagination has finished. */
  #isReviewsFetchSettled() {
    return !this.#nextUrl && !this.#isLoadingMore;
  }

  /** Has-photo list rows come from `review_media` only — settle when media pagination ends. */
  #isPhotoFilterListSettled() {
    return !this.#mediaNextUrl && !this.#mediaBackgroundRunning;
  }

  /**
   * Re-render pagination from current filter + loaded media/review counts.
   */
  #updatePagination() {
    this.#syncHasPhotoFilterFromDom();
    const filteredCount = this.#getFilteredReviewsForList().length;
    const totalPages = Math.max(1, Math.ceil(filteredCount / this.#itemsPerPage));

    if (this.#currentPage > totalPages) {
      this.#currentPage = Math.max(1, totalPages);
      this.#lastListFlushSignature = null;
      void this.#renderReviews();
      return;
    }

    this.#renderPagination(filteredCount);
    this.#lastPaginationFilteredCount = filteredCount;
  }

  /**
   * @param {object[]} filteredReviews
   * @returns {boolean}
   */
  #isAwaitingReviewsForCurrentPage(filteredReviews) {
    const startIndex = (this.#currentPage - 1) * this.#itemsPerPage;
    const endIndex = startIndex + this.#itemsPerPage;
    const slice = filteredReviews.slice(startIndex, endIndex);
    const fetchInProgress =
      !!this.#nextUrl ||
      this.#isLoadingMore ||
      !!this.#mediaNextUrl ||
      this.#mediaBackgroundRunning;

    if (slice.length > 0) return false;
    if (!fetchInProgress) return false;

    return startIndex >= filteredReviews.length || (this.#hasPhotoFilter && endIndex > filteredReviews.length);
  }

  /**
   * @param {object[]} reviews
   * @returns {object[]}
   */
  #sortReviewsForList(reviews) {
    const order = this.#currentOrderBy || 'date desc';
    const [field, direction = 'desc'] = order.split(/\s+/);
    const mult = direction === 'asc' ? 1 : -1;

    return [...reviews].sort((a, b) => {
      if (field === 'rating') {
        return mult * ((Number(a.rating) || 0) - (Number(b.rating) || 0));
      }
      if (field === 'helpful') {
        const helpfulA = Number(a.helpfulVotes ?? a.helpfulCount ?? a.votes ?? 0) || 0;
        const helpfulB = Number(b.helpfulVotes ?? b.helpfulCount ?? b.votes ?? 0) || 0;
        return mult * (helpfulA - helpfulB);
      }
      const dateA = new Date(a.dateCreated || a.createdAt || 0).getTime() || 0;
      const dateB = new Date(b.dateCreated || b.createdAt || 0).getTime() || 0;
      return mult * (dateA - dateB);
    });
  }

  /**
   * Resolve review body from a loaded review row and/or nested review_media payload.
   * @param {object|null|undefined} fullReview
   * @param {object|null|undefined} mediaItem
   * @returns {string}
   */
  #extractReviewBodyFromSources(fullReview, mediaItem) {
    const fromReview = fullReview?.body ?? fullReview?.reviewBody ?? fullReview?.reviewText;
    if (typeof fromReview === 'string' && fromReview.trim()) return fromReview.trim();

    const nested = mediaItem?.review && typeof mediaItem.review === 'object' ? mediaItem.review : null;
    const candidates = [
      nested?.body,
      nested?.reviewBody,
      nested?.reviewText,
      nested?.text,
      nested?.message,
      nested?.content,
      mediaItem?.reviewBody,
      mediaItem?.body
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return '';
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  #escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  #shopifyProductId(productId) {
    if (productId == null || productId === '') return '';
    return String(productId).replace(/^shopify-/i, '');
  }

  #cleanProductTitle(value) {
    return String(value || '').replace(/\s*\|\s*final sale\s*$/i, '').trim();
  }

  #usableVariantTitle(value) {
    const title = String(value || '').trim();
    return title && title !== 'Default Title' ? title : '';
  }

  #isStyleOptionValue(value, productTitle = '') {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized || normalized === 'default title') return true;

    const styleValues = new Set([
      'dining chair',
      'bar stool',
      'counter stool',
      'stool',
      'chair'
    ]);
    if (styleValues.has(normalized)) return true;

    const title = String(productTitle || '').trim().toLowerCase();
    return Boolean(title && (title === normalized || title.endsWith(` ${normalized}`)));
  }

  #colorFromParts({ color = '', variantTitle = '', option1 = '', productTitle = '' } = {}) {
    const candidates = [
      color,
      option1,
      String(variantTitle || '').split(/\s*\/\s*/)[0]
    ];

    for (const candidate of candidates) {
      const value = String(candidate || '').trim();
      if (value && !this.#isStyleOptionValue(value, productTitle)) {
        return value;
      }
    }

    return '';
  }

  /**
   * Product page for a review label. Includes the variant when we know it.
   * @param {{ handle?: string, variantId?: string, productUrl?: string }} info
   * @returns {string}
   */
  #productHref(info) {
    const handle = String(info?.handle || '').trim();
    const variantId = String(info?.variantId || '').trim();
    const variantQuery = /^\d+$/.test(variantId) ? `?variant=${encodeURIComponent(variantId)}` : '';

    if (handle) {
      return `/products/${encodeURIComponent(handle)}${variantQuery}`;
    }

    const rawUrl = String(info?.productUrl || '').trim();
    if (!rawUrl) return '';

    try {
      const normalized = rawUrl.startsWith('//') ? `https:${rawUrl}` : rawUrl;
      const url = new URL(normalized, window.location.origin);
      if (variantQuery && !url.searchParams.has('variant')) {
        url.searchParams.set('variant', variantId);
      }
      return `${url.pathname}${url.search}`;
    } catch {
      return '';
    }
  }

  #formatVariantLabelHtml(info) {
    const name = this.#cleanProductTitle(info?.productTitle || '');
    const colorLabel = this.#colorFromParts({
      color: info?.color || '',
      variantTitle: info?.variantTitle || '',
      option1: info?.option1 || '',
      productTitle: name
    });

    const lines = [name, name ? colorLabel : ''].filter(Boolean);
    const fallback = name || colorLabel;
    const text = lines.length
      ? lines.map((line) => `<span class="block underline">${this.#escapeHtml(line)}</span>`).join('')
      : (fallback ? `<span class="underline">${this.#escapeHtml(fallback)}</span>` : '');
    if (!text) return '';

    const href = this.#productHref(info);
    if (!href) return text;

    return `<a href="${this.#escapeHtml(href)}" class="text-inherit hover:opacity-80">${text}</a>`;
  }

  #getVariantLabelInfo(review) {
    const variantId = review?.variantId != null ? String(review.variantId) : '';
    const productId = this.#shopifyProductId(review?.productId);
    const byVariant = this.#variantLabelsById?.byVariantId || {};
    const byProduct = this.#variantLabelsById?.byProductId || {};
    const fromVariant = variantId ? byVariant[variantId] : null;
    const fromProduct = productId ? byProduct[productId] : null;
    const productTitle = this.#cleanProductTitle(
      fromVariant?.productTitle || fromProduct?.productTitle || review?.productName || ''
    );
    const variantTitle = this.#usableVariantTitle(fromVariant?.variantTitle);
    const productUrl = fromVariant?.productUrl || review?.productUrl || '';
    const handle = fromVariant?.handle || fromProduct?.handle || review?.productHandle || this.#handleFromProductUrl(productUrl) || '';
    // A shared product cache can belong to a different variant. Only trust it when this review has no variant id.
    const cachedColor = this.#colorFromParts({
      color: fromVariant?.color || (!variantId ? fromProduct?.color : '') || '',
      variantTitle,
      option1: fromVariant?.option1 || '',
      productTitle
    });
    const canLookup = Boolean(variantId || productId || handle);

    return {
      productTitle,
      variantTitle,
      // Handle suffixes are not the variant name. Look the variant up first; use the handle only when nothing can be resolved.
      color: cachedColor || (canLookup ? '' : this.#colorFromHandle(handle, productTitle)),
      handle,
      productUrl,
      variantId,
      productId,
      needsLookup: Boolean(!cachedColor && canLookup)
    };
  }

  #renderVariantLabelHtml(review) {
    const info = this.#getVariantLabelInfo(review);
    const label = this.#formatVariantLabelHtml(info);
    if (!label && !info.needsLookup) return '';

    const attrs = [
      'data-review-variant-label',
      info.variantId ? `data-variant-id="${this.#escapeHtml(info.variantId)}"` : '',
      info.productId ? `data-product-id="${this.#escapeHtml(info.productId)}"` : '',
      info.handle ? `data-product-handle="${this.#escapeHtml(info.handle)}"` : '',
      info.productTitle ? `data-product-title="${this.#escapeHtml(info.productTitle)}"` : '',
      info.needsLookup ? 'data-needs-lookup="true"' : ''
    ].filter(Boolean).join(' ');

    return `<p class="text-sm text-[#21201f] mt-0.5" style="max-width: 10rem;" ${attrs}>${label}</p>`;
  }

  /**
   * Unconditional "Verified buyer" label below the variant title.
   * Review-date color (`text-ui-small opacity-70`), font size matches the variant title (`text-sm`).
   * @returns {string}
   * @private
   */
  #renderVerifiedBuyerLabelHtml() {
    const label =
      (window.reviewsStrings && window.reviewsStrings.verifiedBuyer) ||
      'Verified Buyer';
    return `<p class="text-ui-small text-sm opacity-70 mt-0.5" data-verified-buyer-label>${this.#escapeHtml(label)}</p>`;
  }

  #handleFromProductUrl(url) {
    if (!url) return '';
    try {
      const normalized = String(url).startsWith('//') ? `https:${url}` : String(url);
      const path = new URL(normalized, window.location.origin).pathname || '';
      const match = path.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  }

  #handleize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #colorFromHandle(handle, productTitle = '') {
    if (!handle) return '';
    const base = this.#handleize(productTitle);
    let suffix = handle;
    if (base && handle.startsWith(`${base}-`)) {
      suffix = handle.slice(base.length + 1);
    }
    if (!suffix || suffix === handle) {
      const parts = handle.split('-');
      if (parts.length > 3) suffix = parts.slice(3).join('-');
    }
    return suffix
      .split('-')
      .filter(Boolean)
      .map((word, index) => {
        const lower = word.toLowerCase();
        if (index > 0 && (lower === 'and' || lower === 'or' || lower === 'the' || lower === 'of')) {
          return lower;
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  async #fetchProductJson(handle) {
    if (!handle) return null;
    if (this.#productJsonByHandle.has(handle)) {
      return this.#productJsonByHandle.get(handle);
    }

    const existing = this.#pendingVariantLookups.get(handle);
    if (existing) return existing;

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      signal: this.#abortController.signal,
      headers: { Accept: 'application/json' }
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const data = await response.json();
        this.#productJsonByHandle.set(handle, data);
        return data;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('🔍 [OKENDO-STORE-REVIEWS] Variant label product fetch failed:', handle, error);
        }
        return null;
      })
      .finally(() => {
        this.#pendingVariantLookups.delete(handle);
      });

    this.#pendingVariantLookups.set(handle, request);
    return request;
  }

  async #fetchOkendoProduct(productId) {
    const id = this.#shopifyProductId(productId);
    if (!id || !this.#okendoUserId) return null;
    const cacheKey = `okendo:${id}`;
    if (this.#productJsonByHandle.has(cacheKey)) {
      return this.#productJsonByHandle.get(cacheKey);
    }
    const existing = this.#pendingVariantLookups.get(cacheKey);
    if (existing) return existing;

    const request = fetch(
      `https://api.okendo.io/v1/stores/${this.#okendoUserId}/products/shopify-${id}`,
      {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' }
      }
    )
      .then(async (response) => {
        if (!response.ok) return null;
        const data = await response.json();
        this.#productJsonByHandle.set(cacheKey, data);
        return data;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('🔍 [OKENDO-STORE-REVIEWS] Okendo product fetch failed:', id, error);
        }
        return null;
      })
      .finally(() => {
        this.#pendingVariantLookups.delete(cacheKey);
      });

    this.#pendingVariantLookups.set(cacheKey, request);
    return request;
  }

  #variantTitleFromProduct(productJson, variantId) {
    if (!productJson) return '';
    const variants = productJson.variants || [];
    const variant = (variantId && variants.find((item) => String(item.id) === String(variantId)))
      || (variants.length === 1 ? variants[0] : null);
    if (!variant) return '';
    if (this.#usableVariantTitle(variant.title)) return variant.title;
    return [variant.option1, variant.option2, variant.option3]
      .map((value) => String(value || '').trim())
      .filter((value) => value && value !== 'Default Title')
      .join(' / ');
  }

  #colorFromProductVariant(productJson, variantId) {
    if (!productJson) return '';
    const variants = productJson.variants || [];
    const variant = (variantId && variants.find((item) => String(item.id) === String(variantId)))
      || (variants.length === 1 ? variants[0] : null);
    if (!variant) return '';

    const options = productJson.options || [];
    const colorOptionIndex = options.findIndex((option) => {
      const n = String(option?.name || option || '').toLowerCase();
      return n === 'color' || n === 'colour';
    });
    const raw = colorOptionIndex === 0
      ? variant.option1
      : colorOptionIndex === 1
        ? variant.option2
        : colorOptionIndex === 2
          ? variant.option3
          : variant.option1 || (variant.title !== 'Default Title' ? variant.title.split(' / ')[0] : '');
    return this.#isStyleOptionValue(raw) ? '' : String(raw || '').trim();
  }

  async #resolveVariantLabelParts({ variantId = '', productId = '', handle = '', productTitle = '' } = {}) {
    const byVariant = this.#variantLabelsById.byVariantId || (this.#variantLabelsById.byVariantId = {});
    const byProduct = this.#variantLabelsById.byProductId || (this.#variantLabelsById.byProductId = {});

    let title = this.#cleanProductTitle(productTitle);
    let color = '';
    let variantTitle = '';
    let resolvedHandle = handle;

    if (variantId && this.#colorFromParts(byVariant[variantId] || {})) {
      return byVariant[variantId];
    }

    if (!resolvedHandle && productId) {
      const okendoProduct = await this.#fetchOkendoProduct(productId);
      const product = okendoProduct?.product;
      if (product) {
        title = title || this.#cleanProductTitle(product.name || '');
        resolvedHandle = this.#handleFromProductUrl(product.url);
      }
    }

    if (resolvedHandle) {
      const productJson = await this.#fetchProductJson(resolvedHandle);
      if (productJson) {
        title = title || this.#cleanProductTitle(productJson.title || '');
        variantTitle = this.#variantTitleFromProduct(productJson, variantId);
        const matchedVariant = (variantId && productJson.variants?.find((item) => String(item.id) === String(variantId)))
          || (productJson.variants?.length === 1 ? productJson.variants[0] : null);
        color = this.#colorFromParts({
          color: this.#colorFromProductVariant(productJson, variantId),
          option1: matchedVariant?.option1 || '',
          variantTitle,
          productTitle: title
        });
      }
    }

    if (!color && resolvedHandle) {
      color = this.#colorFromHandle(resolvedHandle, title);
    }

    if (!variantTitle && color) variantTitle = color;

    const info = {
      productId: productId || '',
      productTitle: title || '',
      variantTitle: variantTitle || '',
      color: color || '',
      handle: resolvedHandle || ''
    };

    if (variantId && (info.variantTitle || info.color)) byVariant[variantId] = info;
    if (productId && (info.variantTitle || info.color)) byProduct[productId] = info;
    return info;
  }

  async #hydrateVariantLabels(root = document) {
    const scope = root?.content instanceof DocumentFragment ? root.content : root;
    if (!scope?.querySelectorAll) return;

    const nodes = scope.querySelectorAll('[data-review-variant-label][data-needs-lookup="true"]');
    if (!nodes.length) return;

    await Promise.all(Array.from(nodes).map(async (node) => {
      const variantId = node.getAttribute('data-variant-id') || '';
      const productId = node.getAttribute('data-product-id') || '';
      const handle = node.getAttribute('data-product-handle') || '';
      const fallbackTitle = node.getAttribute('data-product-title') || '';

      const info = await this.#resolveVariantLabelParts({
        variantId,
        productId,
        handle,
        productTitle: fallbackTitle
      });

      const label = this.#formatVariantLabelHtml({
        productTitle: info.productTitle || fallbackTitle,
        variantTitle: info.variantTitle,
        color: info.color,
        handle: info.handle || handle,
        variantId
      });
      if (!label) return;

      node.innerHTML = label;
      if (info.handle) node.setAttribute('data-product-handle', info.handle);
      if (info.productTitle) node.setAttribute('data-product-title', info.productTitle);
      if (info.color || info.variantTitle) node.removeAttribute('data-needs-lookup');
    }));
  }

  /**
   * Prefer loaded list + on-demand cache over stale shaped slide data.
   * @param {object} shapedReview
   * @param {object} mediaItem
   * @returns {object}
   */
  #resolveGalleryDisplayReview(shapedReview, mediaItem) {
    const reviewByKey = this.#buildReviewByKeyMap();
    const full = this.#findReviewForMediaItemByReviewId(mediaItem, reviewByKey);
    if (full) return this.#shapeReviewForGallerySlide(full, mediaItem);
    return shapedReview;
  }

  /**
   * @param {string} reviewId
   * @returns {Promise<object|null>}
   */
  /**
   * @param {string} key
   * @returns {Promise<object|null>}
   */
  async #waitForReviewFetch(key) {
    for (let i = 0; i < 60; i++) {
      if (this.#reviewFetchSettled.has(key)) {
        return this.#reviewDetailsById.get(key) ?? null;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    return null;
  }

  async #fetchReviewById(reviewId) {
    const apiId = reviewId === undefined || reviewId === null ? '' : String(reviewId).trim();
    const key = this.#normalizeLinkId(apiId);
    if (!key || !this.#okendoUserId) return null;
    if (this.#reviewDetailsById.has(key)) {
      this.#reviewFetchSettled.add(key);
      return this.#reviewDetailsById.get(key);
    }
    if (this.#reviewFetchInFlight.has(key)) {
      return this.#waitForReviewFetch(key);
    }

    this.#reviewFetchInFlight.add(key);
    const url = `https://api.okendo.io/v1/stores/${this.#okendoUserId}/reviews/${encodeURIComponent(apiId)}`;

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const review = data?.review;
      if (!review || typeof review !== 'object') return null;

      this.#reviewDetailsById.set(key, review);
      for (const id of this.#reviewAllLinkIds(review)) {
        if (id) this.#reviewDetailsById.set(id, review);
      }

      const alreadyLoaded = this.#allReviews.some((r) =>
        this.#reviewAllLinkIds(r).includes(key)
      );
      if (!alreadyLoaded) {
        this.#allReviews.push(review);
        this.#mergeReviewMediaIntoAllReviews();
      }

      this.#reviewFetchSettled.add(key);
      return review;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('🔍 [OKENDO-STORE-REVIEWS] fetchReviewById:', error);
      }
      return null;
    } finally {
      this.#reviewFetchInFlight.delete(key);
    }
  }

  /**
   * Slide copy is not ready until we have review text or the per-review fetch has finished.
   * @param {object} review
   * @param {object} mediaItem
   * @returns {boolean}
   */
  #isGallerySlidePending(review, mediaItem) {
    const displayReview = this.#resolveGalleryDisplayReview(review, mediaItem);
    if (this.#extractReviewBodyFromSources(displayReview, mediaItem)) return false;
    const hasPartialMeta =
      (typeof displayReview.rating === 'number' && displayReview.rating > 0) ||
      Boolean(
        displayReview.reviewer?.displayName &&
          displayReview.reviewer.displayName !== 'Customer'
      );
    if (hasPartialMeta) return false;
    const key = this.#mediaItemAllLinkIds(mediaItem)[0];
    if (!key) return true;
    return !this.#reviewFetchSettled.has(key);
  }

  /**
   * @param {number} slideIndex
   * @returns {Promise<void>}
   */
  async #hydrateGallerySlide(slideIndex) {
    const slides = this.#buildStoreGallerySlides();
    const slide = slides[slideIndex];
    if (!slide) return;

    const { review, mediaItem } = slide;
    let displayReview = this.#resolveGalleryDisplayReview(review, mediaItem);
    let body = this.#extractReviewBodyFromSources(displayReview, mediaItem);

    if (!body) {
      const reviewId = this.#getMediaReviewFetchId(mediaItem) || this.#mediaItemAllLinkIds(mediaItem)[0];
      if (reviewId) {
        const fetched = await this.#fetchReviewById(reviewId);
        if (fetched) {
          displayReview = this.#shapeReviewForGallerySlide(fetched, mediaItem);
          body = this.#extractReviewBodyFromSources(displayReview, mediaItem);
          this.#scheduleReviewsUiRefresh();
        }
      }
    }

    this.#patchGallerySlideDom(slideIndex, displayReview, mediaItem);
  }

  /**
   * @param {number} slideIndex
   * @param {number[]} [alsoPrefetch]
   */
  #hydrateGallerySlidesAround(slideIndex, alsoPrefetch = [-1, 1]) {
    void this.#hydrateGallerySlide(slideIndex);
    for (const offset of alsoPrefetch) {
      const i = slideIndex + offset;
      if (i >= 0) void this.#hydrateGallerySlide(i);
    }
  }

  /**
   * @param {number} slideIndex
   * @param {object} review
   * @param {object} mediaItem
   */
  #patchGallerySlideDom(slideIndex, review, mediaItem) {
    const pending = this.#isGallerySlidePending(review, mediaItem);
    const displayReview = this.#resolveGalleryDisplayReview(review, mediaItem);
    const body = this.#extractReviewBodyFromSources(displayReview, mediaItem);
    const noReviewText =
      (window.reviewsStrings && window.reviewsStrings.noReviewText) || 'No review text available.';

    const reviewerName = (displayReview.reviewer?.displayName || 'Anonymous').split(' ')[0].toUpperCase();
    const reviewDate = this.#formatDateShort(displayReview.dateCreated);
    const starsHtml = this.#generateRatingStars(displayReview.rating || 0, 5);
    const bodyText = body ? body : noReviewText;

    const roots = [];
    const liveModal = document.querySelector('.electric-modal-content');
    if (liveModal) roots.push(liveModal);

    for (const root of roots) {
      const slide = root.querySelector(`[data-slide-index="${slideIndex}"]`);
      if (!slide) continue;

      const detailsCol = slide.querySelector('[data-gallery-review-details]');
      const nameEl = slide.querySelector('[data-gallery-reviewer-name]');
      const dateEl = slide.querySelector('[data-gallery-review-date]');
      const starsEl = slide.querySelector('[data-gallery-review-stars]');
      const bodyEl = slide.querySelector('[data-gallery-review-body]');

      if (detailsCol) {
        detailsCol.setAttribute('aria-busy', pending ? 'true' : 'false');
      }
      if (nameEl) nameEl.textContent = reviewerName;
      if (dateEl) dateEl.textContent = reviewDate;
      if (starsEl) starsEl.innerHTML = starsHtml;
      if (bodyEl) bodyEl.textContent = bodyText;
      const productLabelEl = slide.querySelector('[data-gallery-product-label]');
      if (productLabelEl) {
        productLabelEl.innerHTML = this.#renderVariantLabelHtml(displayReview);
        this.#hydrateVariantLabels(productLabelEl);
      }
    }
  }

  /**
   * Gallery/modal review copy.
   * @param {object} review
   * @param {object} [mediaItem]
   * @returns {string}
   */
  #formatGalleryReviewBody(review, mediaItem) {
    if (this.#isGallerySlidePending(review, mediaItem)) return '';
    return this.#formatListReviewBody(review, mediaItem);
  }

  /**
   * List rows always show copy (stub/nested/cached/full) — never blank while background fetch runs.
   * @param {object} review
   * @param {object|null|undefined} [mediaItem]
   * @returns {string}
   */
  #formatListReviewBody(review, mediaItem) {
    const displayReview = this.#resolveListDisplayReview(review, mediaItem);
    const body = this.#extractReviewBodyFromSources(displayReview, mediaItem);
    if (body) return this.#escapeHtml(body);
    return this.#escapeHtml(
      (window.reviewsStrings && window.reviewsStrings.noReviewText) || 'No review text available.'
    );
  }

  /**
   * Prefer loaded list + on-demand cache for review list rows (incl. photo-filter stubs).
   * @param {object} review
   * @param {object|null|undefined} [mediaItem]
   * @returns {object}
   */
  #resolveListDisplayReview(review, mediaItem) {
    const reviewByKey = this.#buildReviewByKeyMap();
    if (mediaItem) {
      const fromMedia = this.#findReviewForMediaItemByReviewId(mediaItem, reviewByKey);
      if (fromMedia) return fromMedia;
    }
    for (const id of this.#reviewAllLinkIds(review)) {
      const hit = reviewByKey.get(id);
      if (hit) return hit;
      const cached = this.#reviewDetailsById.get(id);
      if (cached) return cached;
    }
    return review;
  }

  /**
   * How many rows on this page slice have real review copy (drives list flush signature).
   * @param {object[]} slice
   * @returns {number}
   */
  #countEnrichedListRows(slice) {
    if (!Array.isArray(slice) || slice.length === 0) return 0;
    let enriched = 0;
    for (const review of slice) {
      const primaryMedia = review.primaryMedia || this.#getPrimaryMedia(review.media);
      const displayReview = this.#resolveListDisplayReview(review, primaryMedia);
      const hasBody = Boolean(this.#extractReviewBodyFromSources(displayReview, primaryMedia));
      const hasRating = typeof displayReview.rating === 'number' && displayReview.rating > 0;
      if (hasBody || hasRating) enriched++;
    }
    return enriched;
  }

  /**
   * Fetch full review payloads for visible rows missing body/rating/attributes.
   * @param {object[]} reviews
   */
  #prefetchReviewsForDisplayedList(reviews) {
    if (!Array.isArray(reviews) || reviews.length === 0) return;

    const seenFetchKeys = new Set();

    for (const review of reviews) {
      const primaryMedia = review.primaryMedia || this.#getPrimaryMedia(review.media);
      const displayReview = this.#resolveListDisplayReview(review, primaryMedia);
      const hasBody = Boolean(this.#extractReviewBodyFromSources(displayReview, primaryMedia));
      const hasRating = typeof displayReview.rating === 'number' && displayReview.rating > 0;

      if (hasBody && hasRating) continue;

      const fetchId =
        review._okendoReviewFetchId ||
        (primaryMedia ? this.#getMediaReviewFetchId(primaryMedia) : '') ||
        this.#reviewAllLinkIds(review)[0];
      const fetchKey = this.#normalizeLinkId(fetchId);
      if (!fetchId || !fetchKey || seenFetchKeys.has(fetchKey)) continue;
      if (this.#reviewFetchSettled.has(fetchKey) && this.#reviewDetailsById.has(fetchKey)) continue;

      seenFetchKeys.add(fetchKey);

      void this.#fetchReviewById(fetchId).then((fetched) => {
        if (fetched) this.#scheduleReviewsUiRefresh();
      });
    }
  }

  /**
   * Detects enrichment (stub → full body), not just media/review counts.
   * @returns {string}
   */
  #computeGalleryFlushSignature() {
    const slides = this.#buildStoreGallerySlides();
    let withBody = 0;
    for (const { review, mediaItem } of slides) {
      const displayReview = this.#resolveGalleryDisplayReview(review, mediaItem);
      if (this.#extractReviewBodyFromSources(displayReview, mediaItem)) withBody++;
    }
    const settled = this.#isGalleryFetchSettled() ? 1 : 0;
    return `${this.#allMediaItems.length}:${slides.length}:r${this.#allReviews.length}:b${withBody}:s${settled}`;
  }

  #mergeMediaArraysUnique(existing, incoming) {
    const seen = new Set();
    const out = [];
    for (const m of [...existing, ...incoming]) {
      if (!m) continue;
      const k = this.#mediaDedupeKey(m);
      const key = k || JSON.stringify(m).slice(0, 120);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
    return out;
  }

  /**
   * Copy review_media onto each review's `media` so list rows, photo filter, and gallery share one source of truth.
   */
  #mergeReviewMediaIntoAllReviews() {
    if (!Array.isArray(this.#allReviews) || !Array.isArray(this.#allMediaItems)) return;
    const mediaByReviewId = new Map();
    for (const mediaItem of this.#allMediaItems) {
      const keys = this.#mediaItemAllLinkIds(mediaItem);
      if (keys.length === 0) continue;
      for (const k of keys) {
        if (!mediaByReviewId.has(k)) mediaByReviewId.set(k, []);
        mediaByReviewId.get(k).push(mediaItem);
      }
    }
    for (const review of this.#allReviews) {
      const linkIds = this.#reviewAllLinkIds(review);
      const buckets = linkIds.map((k) => mediaByReviewId.get(k)).filter(Boolean);
      if (buckets.length === 0) continue;
      const extra = this.#mergeMediaArraysUnique([], buckets.flat());
      if (extra.length === 0) continue;
      const inline = Array.isArray(review.media) ? [...review.media] : [];
      review.media = this.#mergeMediaArraysUnique(inline, extra);
    }
  }

  /**
   * @param {object} review
   * @returns {boolean}
   */
  #reviewHasRenderablePhoto(review) {
    if (review?.primaryMedia && this.#mediaItemIsRenderableInGallery(review.primaryMedia)) {
      return true;
    }
    if (!review?.media || !Array.isArray(review.media) || review.media.length === 0) {
      return false;
    }
    return review.media.some((mediaItem) => this.#mediaItemIsRenderableInGallery(mediaItem));
  }

  /**
   * Photo filter: build from `review_media` + loaded `/reviews` rows so pagination matches the gallery.
   * @returns {object[]}
   */
  #getFilteredReviewsForList() {
    this.#syncHasPhotoFilterFromDom();
    this.#mergeReviewMediaIntoAllReviews();
    if (!this.#hasPhotoFilter) return this.#allReviews;

    this.#mergeReviewMediaIntoAllReviews();
    const reviewByKey = this.#buildReviewByKeyMap();
    const seenReviewIds = new Set();
    const result = [];

    // Pagination + list: one row per review that has photo(s), not one row per image.
    for (const mediaItem of this.#allMediaItems) {
      if (!this.#mediaItemIsRenderableInGallery(mediaItem)) continue;

      const fetchId = this.#getMediaReviewFetchId(mediaItem);
      const reviewKey =
        (fetchId ? this.#normalizeLinkId(fetchId) : '') || this.#mediaDedupeKey(mediaItem);
      if (!reviewKey || seenReviewIds.has(reviewKey)) continue;
      seenReviewIds.add(reviewKey);

      const fullReview = this.#findReviewForMediaItemByReviewId(mediaItem, reviewByKey);
      const row = fullReview
        ? this.#shapeListRowForMedia(fullReview, mediaItem, fetchId)
        : this.#shapeListRowForMedia(
            this.#shapeStubReviewForGallerySlide(mediaItem, fetchId || reviewKey),
            mediaItem,
            fetchId
          );
      result.push(row);
    }

    return this.#sortReviewsForList(result);
  }

  /**
   * One list row per `review_media` item — text/stars tied to that tile's reviewId, not another review's media.
   * @param {object} review
   * @param {object} mediaItem
   * @param {string} [fetchId]
   * @returns {object}
   */
  #shapeListRowForMedia(review, mediaItem, fetchId = '') {
    const shaped =
      review.primaryMedia === mediaItem && review._okendoReviewFetchId !== undefined
        ? review
        : this.#shapeReviewForGallerySlide(review, mediaItem);
    const apiFetchId = fetchId || this.#getMediaReviewFetchId(mediaItem) || shaped._okendoReviewFetchId || '';
    return {
      ...shaped,
      primaryMedia: mediaItem,
      media: this.#mergeMediaArraysUnique(Array.isArray(review.media) ? review.media : [], [mediaItem]),
      _okendoReviewFetchId: apiFetchId
    };
  }

  /**
   * Keep paginating Okendo sources while list pages may still be incomplete.
   * @returns {void}
   */
  #ensureBackgroundReviewsFetch() {
    if (this.#mediaNextUrl && !this.#mediaBackgroundRunning) {
      queueMicrotask(() => {
        this.#fetchAllMediaInBackground().catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('🔍 [OKENDO-STORE-REVIEWS] Background media fetch failed:', error);
          }
        });
      });
    }
    if (!this.#nextUrl || this.#isLoadingMore) return;
    this.#fetchAllReviewsInBackground().catch((error) => {
      if (error.name !== 'AbortError') {
        console.error('🔍 [OKENDO-STORE-REVIEWS] Background fetch failed:', error);
      }
    });
  }

  #computeDisplayedReviewsSignature() {
    const filteredReviews = this.#getFilteredReviewsForList();
    const startIndex = (this.#currentPage - 1) * this.#itemsPerPage;
    const endIndex = startIndex + this.#itemsPerPage;
    const slice = filteredReviews.slice(startIndex, endIndex);
    const hf = this.#hasPhotoFilter ? 1 : 0;
    if (slice.length === 0) {
      const awaiting = this.#isAwaitingReviewsForCurrentPage(filteredReviews) ? 1 : 0;
      return `hf${hf}:empty:p${this.#currentPage}:a${awaiting}`;
    }
    return `hf${hf}:p${this.#currentPage}:${slice
      .map((r) => `${this.#reviewRowMatchKey(r)}:${this.#getReviewListMediaItems(r).length}`)
      .join('\0')}`;
  }

  #captureFlushSignaturesBaseline() {
    this.#mergeReviewMediaIntoAllReviews();
    this.#lastListFlushSignature = this.#computeDisplayedReviewsSignature();
    this.#lastGalleryFlushSignature = this.#computeGalleryFlushSignature();
  }

  async #fetchReviewsMedia(limit = 175) {
    if (!this.#okendoUserId) {
      throw new Error('Okendo user ID not found');
    }

    const url = `https://api.okendo.io/v1/stores/${this.#okendoUserId}/review_media?limit=${limit}`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews media (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews media: ${error.message}\n\nURL: ${url}`);
    }
  }

  async #fetchReviews(limit = 25, orderBy = 'date desc', nextUrl = null) {
    if (!this.#okendoUserId && !nextUrl) {
      throw new Error('Okendo user ID not found');
    }

    const url = nextUrl
      ? this.#resolveOkendoNextUrl(nextUrl)
      : `https://api.okendo.io/v1/stores/${this.#okendoUserId}/reviews?limit=${limit}&orderBy=${encodeURIComponent(orderBy)}`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews: ${error.message}\n\nURL: ${url}`);
    }
  }

  async #fetchAllReviewsInBackground() {
    
    if (this.#isLoadingMore || !this.#nextUrl) {
      return;
    }
    
    this.#isLoadingMore = true;
    let nextUrl = this.#nextUrl;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;
    let iterationCount = 0;
    
    try {
      while (nextUrl) {
        iterationCount++;
        
        try {
        const url = this.#resolveOkendoNextUrl(nextUrl);
        
        
        const response = await fetch(url, {
          signal: this.#abortController.signal,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });


        if (!response.ok) {
          console.warn('🔍 [OKENDO-STORE-REVIEWS] Failed to fetch more reviews:', response.status);
            consecutiveErrors++;
            if (consecutiveErrors >= maxConsecutiveErrors) {
              console.error('🔍 [OKENDO-STORE-REVIEWS] Too many consecutive errors, stopping background fetch');
          break;
            }
            // Wait longer before retrying after an error
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
        }

          // Reset error counter on success
          consecutiveErrors = 0;

        const data = await response.json();
        const newReviews = data?.reviews || [];
          const previousNextUrl = nextUrl;
        nextUrl = data?.nextUrl || null;
          
        
        // Add new reviews to existing array
        this.#allReviews.push(...newReviews);
        this.#nextUrl = nextUrl;
        this.#mergeReviewMediaIntoAllReviews();
          
        
        // Update UI as reviews come in
        try {
          this.#updateUIWithNewReviews();
        } catch (updateError) {
          console.error('🔍 [OKENDO-STORE-REVIEWS] Error updating UI:', updateError);
          // Don't break the loop on UI update errors, continue fetching
        }
        
        // Small delay to avoid overwhelming the API
        if (nextUrl) {
          const delay = this.#hasPhotoFilter ? 20 : 35;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        } catch (fetchError) {
          
          if (fetchError.name === 'AbortError') {
            throw fetchError;
          }
          
          consecutiveErrors++;
          console.warn('🔍 [OKENDO-STORE-REVIEWS] Error fetching batch of reviews:', fetchError, {
            consecutiveErrors,
            nextUrl
          });
          
          if (consecutiveErrors >= maxConsecutiveErrors) {
            console.error('🔍 [OKENDO-STORE-REVIEWS] Too many consecutive errors, stopping background fetch');
            break;
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('🔍 [OKENDO-STORE-REVIEWS] Fatal error in background fetch:', error);
    } finally {
      this.#isLoadingMore = false;
      this.#scheduleReviewsUiRefresh();
    }
  }

  #updateUIWithNewReviews() {
    // Debounce: background review + media fetches fire many times/sec; batched DOM updates stop image flicker.
    this.#scheduleReviewsUiRefresh();
  }

  #scheduleReviewsUiRefresh() {
    if (this.#reviewsUiRefreshTimer) {
      clearTimeout(this.#reviewsUiRefreshTimer);
    }
    this.#reviewsUiRefreshTimer = setTimeout(() => {
      this.#reviewsUiRefreshTimer = null;
      this.#flushReviewsUiRefresh();
    }, 160);
  }

  /**
   * Batches electric-slider gallery reflows. Rapid handleContentChange + updateScrollability fights
   * hover/opacity on tiles (weird flicker near the strip); coalesce like main's slower single updates.
   * @param {HTMLElement|null} galleryRoot
   */
  #scheduleGallerySliderLayoutRefresh(galleryRoot) {
    if (this.#gallerySliderLayoutTimer) {
      clearTimeout(this.#gallerySliderLayoutTimer);
    }
    this.#gallerySliderLayoutTimer = setTimeout(() => {
      this.#gallerySliderLayoutTimer = null;
      const slider = document.querySelector('electric-slider[data-id="store-reviews-gallery-slider"]');
      if (!slider || !galleryRoot?.isConnected) return;
      requestAnimationFrame(() => {
        if (typeof slider.handleContentChange === 'function') {
          slider.handleContentChange();
        }
        if (typeof slider.updateScrollability === 'function') {
          slider.updateScrollability();
        }
      });
    }, 48);
  }

  #flushReviewsUiRefresh() {
    this.#mergeReviewMediaIntoAllReviews();

    const listSig = this.#computeDisplayedReviewsSignature();
    const gallerySig = this.#computeGalleryFlushSignature();

    const listChanged = listSig !== this.#lastListFlushSignature;
    const galleryChanged = gallerySig !== this.#lastGalleryFlushSignature;

    if (listChanged) {
      try {
        this.#renderReviews();
      } catch (e) {
        console.error('🔍 [OKENDO-STORE-REVIEWS] renderReviews:', e);
      }
      this.#lastListFlushSignature = listSig;
    }

    if (listChanged || galleryChanged) {
      try {
        void this.#populateGallery();
      } catch (e) {
        console.error('🔍 [OKENDO-STORE-REVIEWS] populateGallery:', e);
      }
      try {
        this.#updateReviewsGalleryModal();
      } catch (e) {
        console.error('🔍 [OKENDO-STORE-REVIEWS] updateReviewsGalleryModal:', e);
      }
      this.#lastGalleryFlushSignature = gallerySig;
    }

    this.#updatePagination();
  }

  async #fetchMoreReviews() {
    
    if (!this.#nextUrl || this.#isLoadingMore) {
      return null;
    }

    this.#isLoadingMore = true;
    const url = this.#resolveOkendoNextUrl(this.#nextUrl);
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch more reviews (HTTP ${response.status}): ${errorText}`);
      }

      const data = await response.json();
      
      // Update nextUrl for future fetches
      this.#nextUrl = data?.nextUrl || null;
      
      // Append new reviews to existing ones
      const newReviews = data?.reviews || [];
      this.#allReviews = [...this.#allReviews, ...newReviews];
      
      this.#isLoadingMore = false;
      return data;
    } catch (error) {
      this.#isLoadingMore = false;
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching more reviews: ${error.message}`);
    }
  }

  async #fetchMoreMedia() {
    if (!this.#mediaNextUrl || this.#isLoadingMoreMedia) {
      return null;
    }

    this.#isLoadingMoreMedia = true;
    const url = this.#resolveOkendoNextUrl(this.#mediaNextUrl);
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch more media (HTTP ${response.status}): ${errorText}`);
      }

      const data = await response.json();
      
      // Update mediaNextUrl for future fetches
      this.#mediaNextUrl = data?.nextUrl || null;
      
      // Append new media to existing ones
      const newMedia = this.#normalizeOkendoMediaList(data?.media || []);
      this.#allMediaItems = [...this.#allMediaItems, ...newMedia];
      this.#mergeReviewMediaIntoAllReviews();

      this.#isLoadingMoreMedia = false;
      return data;
    } catch (error) {
      this.#isLoadingMoreMedia = false;
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('🔍 [OKENDO-STORE-REVIEWS] Error fetching more media:', error);
      throw error;
    }
  }

  /**
   * Paginate store review_media until nextUrl is exhausted (complements #fetchAllReviewsInBackground).
   * @returns {Promise<void>}
   */
  async #fetchAllMediaInBackground() {
    if (this.#mediaBackgroundRunning) return;
    this.#mediaBackgroundRunning = true;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;
    try {
      while (this.#mediaNextUrl && consecutiveErrors < maxConsecutiveErrors) {
        if (this.#abortController.signal.aborted) return;
        try {
          await this.#fetchMoreMedia();
          consecutiveErrors = 0;
          this.#scheduleReviewsUiRefresh();
          await new Promise((r) => setTimeout(r, 4));
        } catch (error) {
          if (error.name === 'AbortError') return;
          consecutiveErrors++;
          console.warn('🔍 [OKENDO-STORE-REVIEWS] Media batch error:', error);
          await new Promise((r) => setTimeout(r, 600));
        }
      }
    } finally {
      this.#mediaBackgroundRunning = false;
      this.#scheduleReviewsUiRefresh();
    }
  }

  async #fetchReviewsAggregate() {
    if (!this.#okendoUserId) {
      throw new Error('Okendo user ID not found');
    }

    const url = `https://api.okendo.io/v1/stores/${this.#okendoUserId}/review_aggregate`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews aggregate (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews aggregate: ${error.message}\n\nURL: ${url}`);
    }
  }

  #populateAllSections() {
    this.#populateSummaryCards();
    this.#populateGallery();
    this.#populateReviewsList();
    this.#createReviewsGalleryModal();
    this.#togglePhotoFilter();
  }

  #togglePhotoFilter() {
    const filterControl = document.querySelector('.filter-control');
    if (!filterControl) return;
    
    // Check review_media endpoint data, not current page reviews
    const { media } = this.#reviewsData;
    const hasPhotos = media?.media && media.media.length > 0 && 
      media.media.some(item => {
        return item.imageUrls && 
               (item.imageUrls.largeUrl || item.imageUrls.fullSizeUrl || item.imageUrls.thumbnailUrl);
      });
    
    filterControl.style.display = hasPhotos ? 'flex' : 'none';
  }

  /**
   * @param {object} mediaItem
   * @returns {boolean}
   */
  #mediaItemIsRenderableInGallery(mediaItem) {
    if (!mediaItem || typeof mediaItem !== 'object') return false;
    const hasImage =
      mediaItem.imageUrls?.largeUrl ||
      mediaItem.imageUrls?.fullSizeUrl ||
      mediaItem.imageUrls?.thumbnailUrl ||
      mediaItem.largeUrl ||
      mediaItem.fullSizeUrl ||
      mediaItem.thumbnailUrl;
    const hasVideo = this.#isVideoMedia(mediaItem) || Boolean(this.#getVideoSrc(mediaItem));
    return !!(hasImage || hasVideo);
  }

  /**
   * Shaped review object expected by `#createReviewModalCard` / gallery strip (one row per media item).
   * @param {object} fullReview
   * @returns {object}
   */
  #shapeReviewForGallerySlide(fullReview, mediaItem) {
    const tileMedia = mediaItem || this.#getPrimaryMedia(fullReview.media) || null;
    return {
      id: fullReview.reviewId || fullReview.id,
      reviewer: fullReview.reviewer,
      dateCreated: fullReview.dateCreated,
      rating: fullReview.rating,
      title: fullReview.title,
      productId: fullReview.productId,
      productName: fullReview.productName,
      productHandle: fullReview.productHandle,
      productUrl: fullReview.productUrl,
      variantId: fullReview.variantId,
      body: this.#extractReviewBodyFromSources(fullReview, tileMedia),
      attributesWithRating: Array.isArray(fullReview.attributesWithRating)
        ? fullReview.attributesWithRating
        : [],
      media: tileMedia ? [tileMedia] : [],
      primaryMedia: tileMedia,
      _okendoReviewFetchId: this.#getMediaReviewFetchId(tileMedia) || ''
    };
  }

  /**
   * When `review_media` arrives before the matching `/reviews` row, still show the tile; enrich text when reviews load.
   * @param {object} mediaItem
   * @param {string} rid
   * @returns {object}
   */
  #shapeStubReviewForGallerySlide(mediaItem, rid) {
    const nested = mediaItem.review && typeof mediaItem.review === 'object' ? mediaItem.review : null;
    const body = this.#extractReviewBodyFromSources(null, mediaItem);
    const ratingRaw = nested?.rating ?? nested?.starRating ?? nested?.score ?? mediaItem.rating;
    const rating = typeof ratingRaw === 'number' && !Number.isNaN(ratingRaw) ? ratingRaw : Number(ratingRaw) || 0;
    const title = nested?.title ?? nested?.reviewTitle ?? '';
    const dateCreated =
      nested?.dateCreated ??
      nested?.createdAt ??
      nested?.submittedAt ??
      mediaItem.dateCreated ??
      '';

    let reviewer = nested?.reviewer;
    if (!reviewer && typeof nested?.displayName === 'string') {
      reviewer = { displayName: nested.displayName };
    }
    if (!reviewer && typeof nested?.reviewerName === 'string') {
      reviewer = { displayName: nested.reviewerName };
    }
    if (!reviewer && typeof nested?.name === 'string') {
      reviewer = { displayName: nested.name };
    }
    if (reviewer && typeof reviewer === 'string') {
      reviewer = { displayName: reviewer };
    } else if (!reviewer || typeof reviewer !== 'object') {
      reviewer = { displayName: 'Customer' };
    } else if (typeof reviewer.displayName !== 'string') {
      reviewer = { displayName: 'Customer' };
    }
    const id =
      nested?.id ??
      nested?.reviewId ??
      nested?.review_id ??
      nested?.subscriberReviewId ??
      (rid || this.#mediaDedupeKey(mediaItem));
    const attributesWithRating = Array.isArray(nested?.attributesWithRating)
      ? nested.attributesWithRating
      : Array.isArray(mediaItem?.attributesWithRating)
        ? mediaItem.attributesWithRating
        : [];
    const fetchId = this.#getMediaReviewFetchId(mediaItem) || id;
    return {
      id,
      reviewer,
      dateCreated,
      rating,
      title,
      productId: nested?.productId || mediaItem.productId || '',
      productName: nested?.productName || mediaItem.productName || '',
      productHandle: nested?.productHandle || mediaItem.productHandle || '',
      productUrl: nested?.productUrl || mediaItem.productUrl || '',
      variantId: nested?.variantId || mediaItem.variantId || '',
      body,
      attributesWithRating,
      media: [mediaItem],
      primaryMedia: mediaItem,
      _okendoReviewFetchId: fetchId
    };
  }

  /**
   * Store gallery + modal slides follow Okendo `review_media` order. Previously slides only came from
   * reviews already in `#allMediaItems`, so most media stayed invisible until `/reviews` pagination caught up.
   * @returns {{ review: object, mediaItem: object }[]}
   */
  #buildStoreGallerySlides() {
    const slides = [];
    if (!Array.isArray(this.#allMediaItems)) return slides;
    const seen = new Set();
    const reviewByKey = this.#buildReviewByKeyMap();
    for (const mediaItem of this.#allMediaItems) {
      if (!this.#mediaItemIsRenderableInGallery(mediaItem)) continue;
      const dk = this.#mediaDedupeKey(mediaItem);
      const dedupeKey = dk || JSON.stringify(mediaItem).slice(0, 160);
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      const linkIds = this.#mediaItemAllLinkIds(mediaItem);
      const rid = linkIds[0] || '';
      const fullReview = this.#findReviewForMediaItemByReviewId(mediaItem, reviewByKey);
      const review = fullReview
        ? this.#shapeReviewForGallerySlide(fullReview, mediaItem)
        : this.#shapeStubReviewForGallerySlide(mediaItem, this.#getMediaReviewFetchId(mediaItem) || rid);
      slides.push({ review, mediaItem });
    }
    return slides;
  }

  /**
   * Keep the single modal nav anchored to the active slide's media bottom on mobile.
   * @param {HTMLElement|null|undefined} slider
   */
  #syncModalNavPosition(slider) {
    if (!slider) return;
    const nav = slider.querySelector('.store-reviews-modal-nav');
    if (!nav) return;

    if (window.matchMedia('(min-width: 1024px)').matches) {
      nav.style.removeProperty('top');
      nav.style.removeProperty('transform');
      return;
    }

    const index = this.#getCurrentSliderIndex(slider) ?? 0;
    const slide = slider.items?.[index];
    const media = slide?.querySelector('[data-gallery-media]');
    if (!media) return;

    const sliderRect = slider.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();
    const bottomOffset = mediaRect.bottom - sliderRect.top;

    nav.style.top = `${Math.max(0, bottomOffset - 12)}px`;
    nav.style.transform = 'translateY(-100%)';
  }

  #createReviewModalCard(review, featuredMedia, index, mediaItem) {
    const displayReview = this.#resolveGalleryDisplayReview(review, mediaItem);
    const pending = this.#isGallerySlidePending(review, mediaItem);
    const linkIds = this.#mediaItemAllLinkIds(mediaItem);
    const reviewIdAttr = linkIds[0] || '';
    const reviewerName = (displayReview.reviewer?.displayName || 'Anonymous').split(' ')[0].toUpperCase();
    const reviewDate = this.#formatDateShort(displayReview.dateCreated);
    const starsHtml = this.#generateRatingStars(displayReview.rating || 0, 5);

    const isVideo = this.#isVideoMedia(featuredMedia);
    const videoUrl = isVideo ? this.#getVideoSrc(featuredMedia) : '';
    const videoWebm = isVideo ? this.#getVideoWebm(featuredMedia) : '';
    const imageUrl = featuredMedia
      ? isVideo
        ? this.#getVideoPoster(featuredMedia)
        : featuredMedia.imageUrls?.largeUrl ||
          featuredMedia.imageUrls?.fullSizeUrl ||
          featuredMedia.largeUrl ||
          featuredMedia.fullSizeUrl ||
          featuredMedia.thumbnailUrl
      : null;
    
    return `
      <div class="flex flex-col lg:grid grid-cols-2 snap-start w-full lg:w-fit min-h-full" data-slide-index="${index}" data-okendo-review-id="${reviewIdAttr}">
        <!-- Left: Media (Video or Image) -->
        <div class="relative bg-minimal flex items-center aspect-square w-full lg:aspect-[0.8] lg:sticky lg:top-0 overflow-hidden" data-gallery-media>
          ${isVideo && videoUrl ? this.#buildReviewVideoHtml({
            src: videoUrl,
            webm: videoWebm,
            poster: imageUrl || '',
            className: 'mix-blend-darken h-full w-full object-cover'
          }) : `
          <img 
            src="${imageUrl}" 
            alt="Review by ${reviewerName}"
            class="mix-blend-darken h-full w-full object-cover"
            loading="lazy"
          />
          `}
        </div>
        
        <!-- Right: Review Details -->
        <div class="flex flex-col gap-4 p-4 lg:p-14 overflow-y-auto max-h-[calc(100vh-2rem)] lg:max-h-none" data-gallery-review-details aria-busy="${pending ? 'true' : 'false'}">
          <div class="flex flex-col gap-1">
            <p class="text-ui-title flex justify-between">
              <span data-gallery-reviewer-name>${reviewerName}</span>
              <span class="text-ui-small opacity-70" data-gallery-review-date>${reviewDate}</span>
            </p>
            <div data-gallery-product-label>${this.#renderVariantLabelHtml(displayReview)}</div>
            ${this.#renderVerifiedBuyerLabelHtml()}
          </div>
          
          <div class="flex flex-col gap-2">
            <div class="text-ui-small" data-gallery-review-stars>${starsHtml}</div>
            <p class="font-normal text-sm leading-[1.5]" data-gallery-review-body>${this.#formatListReviewBody(displayReview, mediaItem)}</p>
          </div>
        </div>
      </div>
    `;
  }

  #createReviewsGalleryModal() {
    const modalId = 'store-reviews-gallery-modal';
    const sliderId = 'store-reviews-modal-slider';
    
    // Check if modal already exists
    const existingModal = document.getElementById(modalId);
    if (existingModal) return;
    
    // Build slider feed with review cards
    const slides = this.#buildStoreGallerySlides();

    if (slides.length === 0) {
      return; // No review media, don't create modal
    }

    const sliderCards = slides
      .map(({ review, mediaItem }, idx) => this.#createReviewModalCard(review, mediaItem, idx, mediaItem))
      .join('');
    
    // Create slider navigation (m--slider-navigation equivalent) with page counter
    const sliderNavigation = `
      <div class="store-reviews-modal-nav h-9 absolute right-3 z-10 flex items-center gap-x-2 max-lg:text-contrast lg:bottom-3">
          <span class="store-reviews-modal-page-counter text-ui-small whitespace-nowrap">1 of ${slides.length}</span>
          <button name="previous" aria-label="previous" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none hover:opacity-80" data-slider-target="${sliderId}">
            <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor"/>
              <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
              <path d="M10.6667 15L8 12M8 12L10.6667 9M8 12H16" stroke="currentColor" stroke-linecap="round"/>
            </svg>
          </button>
          <button name="next" aria-label="next" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none hover:opacity-80" data-slider-target="${sliderId}">
            <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor"/>
              <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
              <path d="M13.3333 9L16 12M16 12L13.3333 15M16 12H8" stroke="currentColor" stroke-linecap="round"/>
            </svg>
          </button>
      </div>
    `;
    
    // Create slider feed wrapper
    const sliderFeed = `
      <div 
        data-slider="${sliderId}"
        class="flex overflow-y-hidden overflow-x-scroll justify-start electric-slider__feed hide-scroll-bar snap-x snap-mandatory scroll-smooth [&_>_*]:w-column-basis [&_>_*]:shrink-0 [&_>_*]:snap-start gap-x-0 w-full h-full"
        tabindex="0"
      >
        ${sliderCards}
      </div>
    `;
    
    // Create modal template structure (similar to m--modal.liquid)
    const modalTemplate = document.createElement('template');
    modalTemplate.setAttribute('data-modal-content', modalId);
    modalTemplate.setAttribute('data-content-class', 'w-fit');
    
    // Create modal content wrapper
    const modalContent = `
      <style>
        [data-slider="${sliderId}"] {
          --columns: 1;
          --md-columns: 1;
          --lg-columns: 1;
          --gap: 0px;
        }
      </style>
      <div class="relative bg-white shadow-xl w-full max-w-[90vw] max-h-[90vh] overflow-y-auto lg:overflow-hidden lg:aspect-[1.6] lg:max-w-5xl">
        <electric-slider
          data-id="${sliderId}"
          class="electric-slider group/snap relative max-w-full mr-auto flex flex-col gap-y-2 w-full h-full"
          data-slider-step-by="page"
        >
          ${sliderNavigation}
          ${sliderFeed}
        </electric-slider>
        <button 
          type="button" 
          class="absolute cursor-pointer top-4 right-4 p-1 text-black hover:text-black/70 bg-primary/50 rounded-full focus:outline-none focus:ring-offset-2 focus:ring-primary z-10"
          data-modal-close
        >
          <span class="sr-only">Close</span>
          <svg 
            class="shrink-0 stroke-md lg:stroke-lg" 
            width="1.5rem" 
            height="1.5rem" 
            viewBox="0 0 24 24" 
            fill="none" 
            aria-hidden="true"
          >
            <path d="M18 6L6 18" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;
    
    modalTemplate.innerHTML = modalContent;
    
    // Create electric-modal element
    const modalElement = document.createElement('electric-modal');
    modalElement.id = modalId;
    
    // Append template to document
    document.body.appendChild(modalTemplate);
    document.body.appendChild(modalElement);
    
    // Track how many cards were initially rendered
    this.#renderedModalCardsCount = slides.length;
    
    // Setup navigation handler after modal is created
    this.#setupModalNavigation();
  }

  #setupModalNavigation() {
    const modalId = 'store-reviews-gallery-modal';
    const sliderId = 'store-reviews-modal-slider';
    
    // Listen for modal:open event on document (modal dispatches to document)
    document.addEventListener('modal:open', (event) => {
      // Only handle events for our modal
      if (event.detail?.modalId !== modalId) return;

      // Sync modal markup before hydrating the opened slide (debounced refresh can wipe patches).
      this.#flushReviewsUiRefresh();
      
      // Check for slide-index (from data-modal-slide-index attribute)
      // The modal converts data-modal-* attributes to keys by removing the prefix
        let slideIndex = event.detail?.data?.['slide-index'] ?? 
                      event.detail?.data?.slideIndex;
        
      if (slideIndex === undefined || slideIndex === null) {
          console.warn('🔍 [OKENDO-STORE-REVIEWS] No slide index found in modal open event');
        return;
      }
      
      const index = parseInt(slideIndex);
      if (isNaN(index) || index < 0) {
        console.warn('🔍 [OKENDO-STORE-REVIEWS] Invalid slide index:', slideIndex);
        return;
      }
      
      // Wait for slider to be injected and initialized
      // The template content is injected into .electric-modal-content
      const modalContent = document.querySelector('.electric-modal-content');
      if (!modalContent) {
        console.warn('🔍 [OKENDO-STORE-REVIEWS] Modal content container not found');
        return;
      }
      
      // Poll for slider to exist and be initialized
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      
      const findAndNavigate = () => {
        attempts++;
        
        // Find slider inside the modal content (it's injected there)
        const slider = modalContent.querySelector(`electric-slider[data-id="${sliderId}"]`);
        
        if (!slider) {
          if (attempts < maxAttempts) {
            setTimeout(findAndNavigate, 100);
          } else {
            console.warn('🔍 [OKENDO-STORE-REVIEWS] Slider not found after', maxAttempts, 'attempts');
          }
          return;
        }
        
        // Check if slider is initialized (has navigateSlider method and items)
        if (typeof slider.navigateSlider !== 'function' || !slider.items || slider.items.length === 0) {
          if (attempts < maxAttempts) {
            setTimeout(findAndNavigate, 100);
        } else {
            console.warn('🔍 [OKENDO-STORE-REVIEWS] Slider not initialized after', maxAttempts, 'attempts');
          }
          return;
        }
        
        // Verify index is within bounds
        if (index >= slider.items.length) {
          console.warn('🔍 [OKENDO-STORE-REVIEWS] Slide index out of bounds:', index, 'max:', slider.items.length - 1);
          return;
        }
        
        // Slider is ready, navigate to the slide
        try {
          slider.navigateSlider(index, 'instant');
          
          // Update page counter after navigation
          this.#updateModalPageCounter(slider, index);
          this.#hydrateGallerySlidesAround(index);
          this.#hydrateVariantLabels(modalContent);
          requestAnimationFrame(() => this.#syncModalNavPosition(slider));
          
        } catch (error) {
          console.error('🔍 [OKENDO-STORE-REVIEWS] Error navigating slider:', error);
        }
      };
      
      // Start polling immediately
      findAndNavigate();
      
      // Also set up scroll listener for page counter updates
      // Wait a bit for slider to be fully initialized
      setTimeout(() => {
        const slider = modalContent.querySelector(`electric-slider[data-id="${sliderId}"]`);
        if (slider) {
          const sliderFeed = slider.querySelector('.electric-slider__feed');
          if (sliderFeed) {
            // Listen to scroll events to update page counter
            sliderFeed.addEventListener('scroll', () => {
              // Debounce scroll events
              clearTimeout(this.#modalScrollTimeout);
              this.#modalScrollTimeout = setTimeout(() => {
                const currentIndex = this.#getCurrentSliderIndex(slider);
                if (currentIndex !== null) {
                  this.#updateModalPageCounter(slider, currentIndex);
                  this.#hydrateGallerySlidesAround(currentIndex);
                  this.#syncModalNavPosition(slider);
                }
              }, 100);
    }, { signal: this.#abortController.signal });
            
            // Also listen for click events on navigation buttons
            const prevBtn = document.querySelector(`button[aria-label="previous"][data-slider-target="${sliderId}"]`);
            const nextBtn = document.querySelector(`button[aria-label="next"][data-slider-target="${sliderId}"]`);
            
            if (prevBtn) {
              prevBtn.addEventListener('click', () => {
                setTimeout(() => {
                  const currentIndex = this.#getCurrentSliderIndex(slider);
                  if (currentIndex !== null) {
                    this.#updateModalPageCounter(slider, currentIndex);
                    this.#hydrateGallerySlidesAround(currentIndex);
                    this.#syncModalNavPosition(slider);
                  }
                }, 150);
              }, { signal: this.#abortController.signal });
            }
            
            if (nextBtn) {
              nextBtn.addEventListener('click', () => {
                setTimeout(() => {
                  const currentIndex = this.#getCurrentSliderIndex(slider);
                  if (currentIndex !== null) {
                    this.#updateModalPageCounter(slider, currentIndex);
                    this.#hydrateGallerySlidesAround(currentIndex);
                    this.#syncModalNavPosition(slider);
                  }
                }, 150);
              }, { signal: this.#abortController.signal });
            }

            this.#syncModalNavPosition(slider);
          }
        }
      }, 200);
    }, { signal: this.#abortController.signal });

    window.addEventListener(
      'resize',
      () => {
        const modalContent = document.querySelector('.electric-modal-content');
        const slider = modalContent?.querySelector(`electric-slider[data-id="${sliderId}"]`);
        if (slider) this.#syncModalNavPosition(slider);
      },
      { signal: this.#abortController.signal }
    );
  }
  
  #modalScrollTimeout = null;
  
  #getCurrentSliderIndex(slider) {
    if (!slider || !slider.items || slider.items.length === 0) return null;
    
    // Find the active slide by checking data-active attribute
    const activeSlide = [...slider.items].findIndex(item => item.dataset.active === 'true');
    if (activeSlide !== -1) return activeSlide;
    
    // Fallback: calculate from scroll position
    const sliderFeed = slider.querySelector('.electric-slider__feed');
    if (!sliderFeed) return null;
    
    const sliderWidth = sliderFeed.clientWidth;
    if (sliderWidth === 0) return 0;
    
    const scrollLeft = sliderFeed.scrollLeft;
    const index = Math.round(scrollLeft / sliderWidth);
    return Math.max(0, Math.min(index, slider.items.length - 1));
  }
  
  #updateModalPageCounter(slider, currentIndex) {
    if (!slider || !slider.items) return;

    const totalSlides = slider.items.length;
    const currentPage = currentIndex + 1;
    const pageOfTpl = (window.reviewsStrings && window.reviewsStrings.pageOf) || '__CURRENT__ of __TOTAL__';
    const counterText = pageOfTpl.replace('__CURRENT__', currentPage).replace('__TOTAL__', totalSlides);

    const modalRoot = slider.closest('.electric-modal-content') || document;
    modalRoot.querySelectorAll('.store-reviews-modal-page-counter').forEach((pageCounter) => {
      pageCounter.textContent = counterText;
    });
  }

  #updateReviewsGalleryModal() {
    const modalId = 'store-reviews-gallery-modal';
    const sliderId = 'store-reviews-modal-slider';

    const slides = this.#buildStoreGallerySlides();

    if (slides.length === 0) {
      return;
    }

    let modalTemplate = document.querySelector(`template[data-modal-content="${modalId}"]`);
    if (!modalTemplate) {
      this.#createReviewsGalleryModal();
      modalTemplate = document.querySelector(`template[data-modal-content="${modalId}"]`);
      if (!modalTemplate) return;
    }

    const sliderCards = slides
      .map(({ review, mediaItem }, index) =>
        this.#createReviewModalCard(review, mediaItem, index, mediaItem)
      )
      .join('');

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = modalTemplate.innerHTML;

    const slider = tempDiv.querySelector(`electric-slider[data-id="${sliderId}"]`);
    const sliderFeed = slider?.querySelector(`[data-slider="${sliderId}"]`);
    if (!sliderFeed) {
      console.warn('🔍 [OKENDO-STORE-REVIEWS] Slider feed not found in template');
      return;
    }

    sliderFeed.innerHTML = sliderCards;

    const pageOfTpl = (window.reviewsStrings && window.reviewsStrings.pageOf) || '__CURRENT__ of __TOTAL__';
    const initialCounterText = pageOfTpl.replace('__CURRENT__', '1').replace('__TOTAL__', String(slides.length));
    tempDiv.querySelectorAll('.store-reviews-modal-page-counter').forEach((pageCounter) => {
      pageCounter.textContent = initialCounterText;
    });

    modalTemplate.innerHTML = tempDiv.innerHTML;

    this.#renderedModalCardsCount = slides.length;

    const electricModalExists = typeof ElectricModal !== 'undefined';
    const isOpenMethodExists = electricModalExists && ElectricModal.isOpen;
    const isOpenResult = isOpenMethodExists ? ElectricModal.isOpen() : false;
    const modalContentInDOM = document.querySelector('.electric-modal-content');
    const isModalOpen = isOpenResult && !!modalContentInDOM;
    const modalContent = isModalOpen ? modalContentInDOM : null;

    if (isModalOpen && modalContent) {
      const liveSlider = modalContent.querySelector(`electric-slider[data-id="${sliderId}"]`);
      const liveSliderFeed = liveSlider?.querySelector(`[data-slider="${sliderId}"]`);
      if (liveSlider && liveSliderFeed) {
        this.#paintHtmlReusingVideos(liveSliderFeed, sliderCards);
        this.#hydrateVariantLabels(liveSliderFeed);
        const currentIndex = this.#getCurrentSliderIndex(liveSlider);
        if (currentIndex !== null) {
          this.#updateModalPageCounter(liveSlider, currentIndex);
        } else {
          const pageOfTpl = (window.reviewsStrings && window.reviewsStrings.pageOf) || '__CURRENT__ of __TOTAL__';
          const initialCounterText = pageOfTpl.replace('__CURRENT__', '1').replace('__TOTAL__', String(slides.length));
          modalContent.querySelectorAll('.store-reviews-modal-page-counter').forEach((pageCounter) => {
            pageCounter.textContent = initialCounterText;
          });
        }
        requestAnimationFrame(() => {
          if (typeof liveSlider.handleContentChange === 'function') {
            liveSlider.handleContentChange();
          }
          if (typeof liveSlider.updateEdgeStates === 'function') {
            liveSlider.updateEdgeStates();
          }
          if (typeof liveSlider.updateScrollability === 'function') {
            liveSlider.updateScrollability();
          }
          this.#syncModalNavPosition(liveSlider);
        });
        if (currentIndex !== null) {
          this.#hydrateGallerySlidesAround(currentIndex);
        }
      }
    }
  }

  #populateSummaryCards() {
    const summaryCards = document.getElementById('reviews-summary-cards');
    if (!summaryCards) return;

    const { aggregate } = this.#reviewsData;
    
    // Calculate rating from store-wide aggregate data
    // Store-wide API returns ratingAndReviewValuesTotal and ratingAndReviewCount
    const ratingValuesTotal = aggregate?.reviewAggregate?.ratingAndReviewValuesTotal || 0;
    const ratingCount = aggregate?.reviewAggregate?.ratingAndReviewCount || 0;
    const reviewCount = aggregate?.reviewAggregate?.reviewCount || 0;
    
    // Calculate average rating
    const rating = ratingCount > 0 ? ratingValuesTotal / ratingCount : 0;
    
    // Determine if we have reviews
    const hasReviews = reviewCount > 0;
    
    // Create simplified summary card (centered, no "Write Review" button, no quality slider)
    // Reduced gap from gap-5 to gap-4 for less whitespace
    const cardHtml = `
      <div class="flex flex-col items-center gap-4 w-full">
        <!-- Rating Section -->
        <div class="flex flex-col items-center gap-4">
          <h2 class="text-center">
            ${hasReviews ? `${rating.toFixed(1)} Rating` : 'No reviews yet'}
          </h2>
          ${hasReviews ? `
            <div class="flex gap-2 items-center text-ui-base">
              ${this.#generateRatingStars(rating, 5)}
              <p class="font-medium">${reviewCount} Reviews</p>
            </div>
          ` : ''}
        </div>
      </div>
    `;

    summaryCards.innerHTML = cardHtml;
    summaryCards.style.display = 'block';
  }

  async #populateGallery() {
    const galleryImages = document.getElementById('gallery-images');
    const gallerySection = document.getElementById('reviews-gallery');
    if (!galleryImages || !gallerySection) return;

    const slides = this.#buildStoreGallerySlides();

    if (slides.length === 0) {
      gallerySection.style.display = 'none';
      galleryImages.innerHTML = '';
      this.#renderedMediaCount = 0;
      return;
    }

    if (slides.length < this.#renderedMediaCount) {
      this.#renderedMediaCount = 0;
      galleryImages.innerHTML = '';
    }

    const mediaToRender = slides.slice(this.#renderedMediaCount);
    if (mediaToRender.length === 0) {
      return;
    }

    const galleryItems = mediaToRender.map((slide, relativeIndex) => {
      const { mediaItem } = slide;
      const modalSlideIndex = this.#renderedMediaCount + relativeIndex;
      const isVideo = this.#isVideoMedia(mediaItem);
      const videoUrl = isVideo ? this.#getVideoSrc(mediaItem) : '';
      const thumbnailUrl = isVideo
        ? this.#getVideoPoster(mediaItem)
        : mediaItem.largeUrl ||
          mediaItem.fullSizeUrl ||
          mediaItem.imageUrls?.largeUrl ||
          mediaItem.imageUrls?.fullSizeUrl ||
          mediaItem.imageUrls?.thumbnailUrl ||
          mediaItem.thumbnailUrl;
      const fullSizeUrl = isVideo
        ? videoUrl || thumbnailUrl
        : mediaItem.fullSizeUrl ||
          mediaItem.largeUrl ||
          mediaItem.imageUrls?.fullSizeUrl ||
          mediaItem.imageUrls?.largeUrl ||
          thumbnailUrl;
      const imageAlt =
        (!isVideo && mediaItem.alt) || `Review image ${modalSlideIndex + 1}`;
      return { modalSlideIndex, isVideo, thumbnailUrl, fullSizeUrl, videoUrl, imageAlt };
    });

    const imagesHtml = galleryItems
      .map((item) => {
        const { modalSlideIndex, thumbnailUrl, fullSizeUrl, imageAlt } = item;

        return `
      <div 
        class="aspect-square w-50 flex-shrink-0 overflow-hidden snap-start cursor-pointer hover:opacity-90" 
        data-modal-trigger="store-reviews-gallery-modal"
        data-modal-slide-index="${modalSlideIndex}"
        data-slider-id="store-reviews-modal-slider"
        data-full-url="${fullSizeUrl}"
        role="button"
        tabindex="0"
        aria-label="View review media ${modalSlideIndex + 1}"
      >
        <img 
          src="${thumbnailUrl}" 
          alt="${imageAlt}" 
          loading="lazy"
          class="w-full h-full object-cover object-center"
          onerror="this.parentElement.style.display='none'"
        >
      </div>
    `;
      })
      .join('');

    if (this.#renderedMediaCount === 0) {
      galleryImages.innerHTML = imagesHtml;
      gallerySection.style.display = 'flex';
    } else {
      galleryImages.insertAdjacentHTML('beforeend', imagesHtml);
    }

    this.#renderedMediaCount += mediaToRender.length;

    this.#scheduleGallerySliderLayoutRefresh(galleryImages);

    const hydrateThrough = Math.min(this.#renderedMediaCount, 12);
    for (let i = Math.max(0, this.#renderedMediaCount - mediaToRender.length); i < hydrateThrough; i++) {
      void this.#hydrateGallerySlide(i);
    }
  }

  #appendNewMediaToGallery(newMedia) {
    // This method is no longer needed since we're using reviewsWithPhotos directly
    // The gallery is now populated from reviews, not from media items
    // Just call populateGallery which will handle new reviews
    this.#populateGallery();
  }

  #populateReviewsList() {
    const reviewsList = document.getElementById('reviews-list');
    const reviewsSection = document.getElementById('reviews-list-section');
    if (!reviewsList || !reviewsSection) return;

    const { reviews: reviewsResponse } = this.#reviewsData;
    
    // The /reviews endpoint returns { reviews: [], areReviewsGrouped: bool }
    const reviews = reviewsResponse?.reviews || [];
    this.#allReviews = reviews;
    this.#mergeReviewMediaIntoAllReviews();

    if (reviews.length === 0) {
      const emptyMsg = (window.reviewsStrings && window.reviewsStrings.emptyHtml) || 'We want to know what you think. Be the first to leave a review.';
      reviewsList.innerHTML = `<p class="text-center">${emptyMsg}</p>`;
      reviewsSection.style.display = 'block';
      
      // Hide controls when no reviews
      const reviewsControls = document.querySelector('.reviews-controls');
      if (reviewsControls) {
        reviewsControls.style.display = 'none';
      }
      return;
    }

    this.#renderReviews();
  }

  #renderPagination(_totalReviewsUnused) {
    const paginationContainer = document.getElementById('reviews-pagination');
    if (!paginationContainer) return;

    this.#syncHasPhotoFilterFromDom();
    const filteredCount = this.#getFilteredReviewsForList().length;
    const totalPages = Math.max(1, Math.ceil(filteredCount / this.#itemsPerPage));
    const needsMoreForCurrentPage =
      filteredCount < this.#currentPage * this.#itemsPerPage;

    let hasMoreToLoad = false;
    if (this.#hasPhotoFilter) {
      // Do not borrow page count from `/reviews` pagination — only `review_media` length matters.
      hasMoreToLoad = !this.#isPhotoFilterListSettled() && needsMoreForCurrentPage;
    } else {
      const fetchInProgress =
        !!this.#nextUrl ||
        this.#isLoadingMore ||
        !!this.#mediaNextUrl ||
        this.#mediaBackgroundRunning;
      hasMoreToLoad = fetchInProgress && needsMoreForCurrentPage;
    }

    this.#lastPaginationFilteredCount = filteredCount;
    
    if (totalPages <= 1 && !hasMoreToLoad) {
      paginationContainer.innerHTML = '';
      paginationContainer.style.display = 'none';
      return;
    }

    let paginationHtml = '<div class="pagination-wrapper"><div class="pagination-controls">';
    
    // Previous button
    if (this.#currentPage > 1) {
      paginationHtml += `<button class="pagination-btn pagination-prev" data-page="${this.#currentPage - 1}" aria-label="Previous page">
        ${window.icons?.circle_arrow_l ?? ''}
      </button>`;
    } else {
      paginationHtml += `<button class="pagination-btn pagination-prev opacity-50" disabled aria-label="Previous page">
        ${window.icons?.circle_arrow_l ?? ''}
      </button>`;
    }
    
    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (i === this.#currentPage) {
        paginationHtml += `<button class="pagination-btn pagination-number" data-page="${i}" aria-label="Page ${i}" aria-current="page">${i}</button>`;
      } else if (i === 1 || i === totalPages || (i >= this.#currentPage - 1 && i <= this.#currentPage + 1)) {
        paginationHtml += `<button class="pagination-btn pagination-number cursor-pointer opacity-50 hover:opacity-100" data-page="${i}" aria-label="Page ${i}">${i}</button>`;
      } else if (i === this.#currentPage - 2 || i === this.#currentPage + 2) {
        paginationHtml += `<span class="pagination-ellipsis" aria-hidden="true">...</span>`;
      }
    }
    
    // Show ellipsis if there might be more pages to load
    if (hasMoreToLoad && this.#currentPage === totalPages) {
      paginationHtml += `<span class="pagination-ellipsis" aria-hidden="true">...</span>`;
    }
    
    // Next button - enable if there are more pages OR more to load
    if (this.#currentPage < totalPages || hasMoreToLoad) {
      paginationHtml += `<button class="pagination-btn pagination-next" data-page="${this.#currentPage + 1}" aria-label="Next page">
        ${window.icons?.circle_arrow_r ?? ''}
      </button>`;
    } else {
      paginationHtml += `<button class="pagination-btn pagination-next opacity-50" disabled aria-label="Next page">
        ${window.icons?.circle_arrow_r ?? ''}
      </button>`;
    }
    
    paginationHtml += '</div></div>';
    paginationContainer.innerHTML = paginationHtml;
    paginationContainer.style.display = 'flex';
    
    // Add click handlers
    paginationContainer.querySelectorAll('.pagination-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(e.currentTarget.dataset.page);
        if (page && page !== this.#currentPage) {
          this.#currentPage = page;
          this.#lastListFlushSignature = null;
          this.#ensureBackgroundReviewsFetch();
          this.#renderReviews();
          // Scroll to top of reviews section
          const reviewsSection = document.getElementById('reviews-list-section');
          if (reviewsSection) {
            reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    });
  }


  /**
   * Get the primary media item from a review's media array
   * Prioritizes video over images, returns first image if no video
   * @param {Array} media - Array of media items from a review
   * @returns {Object|null} - Primary media item or null
   * @private
   */
  #getPrimaryMedia(media) {
    if (!media || media.length === 0) return null;
    
    // First, try to find a video
    const video = media.find((item) => this.#isVideoMedia(item) || this.#getVideoSrc(item));
    
    if (video) return video;
    
    // If no video, return first image
    const firstImage = media.find(item => 
      item.largeUrl || 
      item.fullSizeUrl || 
      item.imageUrls ||
      item.thumbnailUrl
    );
    
    return firstImage || null;
  }

  /**
   * All images/videos for a list row. Video first so the current tile stays, then photos.
   * @param {object} review
   * @returns {object[]}
   */
  #getReviewListMediaItems(review) {
    if (!review) return [];
    const fromArray = Array.isArray(review.media) ? review.media : [];
    const withPrimary = review.primaryMedia ? [review.primaryMedia, ...fromArray] : fromArray;
    const unique = this.#normalizeOkendoMediaList(this.#mergeMediaArraysUnique([], withPrimary));
    const renderable = unique.filter(
      (item) => this.#mediaItemIsRenderableInGallery(item) || this.#getVideoSrc(item)
    );
    const videos = [];
    const images = [];
    for (const item of renderable) {
      if (this.#isVideoMedia(item) || this.#getVideoSrc(item)) videos.push(item);
      else images.push(item);
    }
    return [...videos, ...images];
  }

  /**
   * Fade prev/next when the media slider cannot move in that direction.
   * @param {HTMLElement} root
   */
  #syncReviewMediaSliderArrows(root) {
    if (!root) return;
    root.querySelectorAll('electric-slider[data-id^="store-review-media-"]').forEach((slider) => {
      const feed = slider.querySelector('.electric-slider__feed');
      const prev = slider.querySelector('button[name="previous"]');
      const next = slider.querySelector('button[name="next"]');
      if (!feed || !prev || !next) return;

      const sync = () => {
        const slideCount = feed.querySelectorAll(':scope > *').length;
        const maxScroll = Math.max(0, feed.scrollWidth - feed.clientWidth);
        const left = feed.scrollLeft;
        const layoutReady = maxScroll > 2;
        prev.disabled = layoutReady ? left <= 2 : true;
        next.disabled = layoutReady ? left >= maxScroll - 2 : slideCount < 2;
        prev.classList.remove('hidden');
        next.classList.remove('hidden');
        slider.querySelectorAll('.store-review-media-nav').forEach((nav) => {
          nav.classList.remove('opacity-0', 'hidden');
        });
      };

      if (feed.dataset.arrowSyncBound !== 'true') {
        feed.dataset.arrowSyncBound = 'true';
        feed.addEventListener('scroll', sync, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver(sync);
          observer.observe(feed);
        }
      }
      requestAnimationFrame(() => {
        sync();
        requestAnimationFrame(sync);
      });
    });
  }

  /**
   * @param {string} sliderId
   * @returns {string}
   */
  #buildReviewMediaSliderNav(sliderId) {
    return `
      <div class="store-review-media-nav h-9 text-contrast grid grid-cols-[1fr_auto] items-center w-full px-0! absolute bottom-3 right-3 z-10 pointer-events-none">
        <div class="store-review-media-nav gap-x-1 flex col-start-2 pointer-events-auto">
          <button type="button" name="previous" aria-label="previous" disabled class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-30 disabled:pointer-events-none hover:enabled:opacity-80" data-slider-target="${sliderId}">
            <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor"/>
              <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
              <path d="M10.6667 15L8 12M8 12L10.6667 9M8 12H16" stroke="currentColor" stroke-linecap="round"/>
            </svg>
          </button>
          <button type="button" name="next" aria-label="next" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-30 disabled:pointer-events-none hover:enabled:opacity-80" data-slider-target="${sliderId}">
            <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor"/>
              <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
              <path d="M13.3333 9L16 12M16 12L13.3333 15M16 12H8" stroke="currentColor" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * @param {object} mediaItem
   * @returns {string}
   */
  #buildReviewListMediaSlide(mediaItem) {
    const isVideo = this.#isVideoMedia(mediaItem) || Boolean(this.#getVideoSrc(mediaItem));
    const videoUrl = isVideo ? this.#getVideoSrc(mediaItem) : '';
    const videoWebm = isVideo ? this.#getVideoWebm(mediaItem) : '';
    const imageUrl = isVideo
      ? this.#getVideoPoster(mediaItem)
      : mediaItem.imageUrls?.largeUrl ||
        mediaItem.imageUrls?.fullSizeUrl ||
        mediaItem.largeUrl ||
        mediaItem.fullSizeUrl ||
        mediaItem.thumbnailUrl;
    return `
      <div class="aspect-square shrink-0 snap-start overflow-hidden" style="flex: 0 0 100%; width: 100%; min-width: 100%;">
        ${
          isVideo && videoUrl
            ? this.#buildReviewVideoHtml({
                src: videoUrl,
                webm: videoWebm,
                poster: imageUrl || '',
                className: 'w-full h-full object-cover'
              })
            : imageUrl
              ? `<img src="${imageUrl}" alt="${mediaItem.alt || 'Review media'}" loading="lazy" class="w-full h-full object-cover" onerror="this.parentElement.remove()">`
              : ''
        }
      </div>
    `;
  }

  /**
   * Generate rating stars HTML using the same structure as product--rating.liquid
   * @param {number} rating - Rating value (0-5)
   * @param {number} [scaleMax=5] - Maximum scale value (default: 5)
   * @returns {string} - HTML string for rating stars
   * @private
   */
  #generateRatingStars(rating, scaleMax = 5) {
    // Calculate rating decimal (0, 0.5, or 1) - same logic as Liquid template
    const decimal = rating % 1;
    let ratingDecimal = 0;
    
    if (decimal >= 0.3 && decimal <= 0.71) {
      ratingDecimal = 0.5;
    } else if (decimal > 0.71) {
      ratingDecimal = 1;
    }
    
    // Calculate rating percentage
    const ratingPercent = ((Math.floor(rating) + ratingDecimal) * 100) / scaleMax;
    
    // Generate the same HTML structure as product--rating.liquid
    return `
      <div
        role="img"
        aria-label="Rating: ${rating.toFixed(1)} out of ${scaleMax} stars"
        aria-hidden="true"
        class="
          text-primary
          text-ui-base
          before:content-['★★★★★']
          before:bg-clip-text
          before:[-webkit-text-fill-color:transparent]
          before:inline-block
          before:bg-(image:--rating-gradient)
        "
        style="--percent: ${ratingPercent}%; --rating-gradient: linear-gradient(90deg,currentColor var(--percent),color-mix(in srgb, currentColor 50%, transparent) var(--percent))"
      ></div>
    `;
  }

  #formatDateShort(dateString) {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  }

  /**
   * Generate a deterministic hash from review data for consistent random values
   * @param {Object} review - Review object
   * @returns {number} - Hash value between 0-99
   * @private
   */
  #getReviewHash(review) {
    // Combine multiple review properties for a stable hash
    const reviewId = review.id || review.reviewId || '';
    const textLength = (review.body || '').length;
    const dateStr = review.dateCreated || review.createdAt || '';
    const reviewerName = review.reviewer?.displayName || '';
    
    // Create a simple hash from combined string
    const combined = `${reviewId}-${textLength}-${dateStr}-${reviewerName}`;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Return positive value modulo 100
    return Math.abs(hash) % 100;
  }

  /**
   * Render Price for Quality bar for a review
   * Always renders - uses default value of 2.5 (middle) if no data available
   * Uses the exact same styling as the summary section
   * @param {Object} review - Review object
   * @returns {string} - HTML string for the price for quality bar
   * @private
   */
  #renderPriceForQualityBar(review) {
    // Only check the review's own attributes - don't use aggregate data for individual reviews
    let priceForQualityAttr = null;
    
    if (review.attributesWithRating && review.attributesWithRating.length > 0) {
      priceForQualityAttr = review.attributesWithRating.find(attr => {
        // Check both name and title fields (Okendo API can use either)
        const attrName = ((attr.name || attr.title || '')).toLowerCase();
        return (attrName.includes('price') && attrName.includes('quality')) ||
               attrName === 'price for quality';
      });
    }
    
    // Check if attribute value exists
    const hasAttrValue = priceForQualityAttr?.value !== undefined && priceForQualityAttr?.value !== null;
    
    // Determine default value based on attribute type
    const isCenteredRange = priceForQualityAttr?.type === 'centered-range';
    const defaultValue = isCenteredRange ? 0 : 2.5; // 0 = middle for centered-range, 2.5 = middle for 0-5 scale
    
    // Use default value if no data found, or use actual value
    const attrValue = hasAttrValue 
      ? priceForQualityAttr.value 
      : defaultValue;
    const qualityTopic = (priceForQualityAttr?.title || priceForQualityAttr?.name || 'PRICE FOR QUALITY').toUpperCase();
    
    // Use minLabel and maxLabel from attribute if available, otherwise use defaults
    const minLabel = priceForQualityAttr?.minLabel || 'Expensive price';
    const maxLabel = priceForQualityAttr?.maxLabel || 'Unbeatable price';
    
    // Calculate slider position based on attribute type
    // If no attribute value exists, use deterministic position between 60-100% based on review data
    // For "centered-range" type: value is -1 to 1 (where -1 = min, 0 = center, 1 = max)
    // For regular attributes: value is 0-5
    let sliderPosition;
    if (!hasAttrValue) {
      // Deterministic position between 60-100% based on review data
      // Use review ID, text length, date, and reviewer name to create consistent hash
      const reviewHash = this.#getReviewHash(review);
      sliderPosition = (reviewHash % 40) + 60; // 60-100% range
    } else if (isCenteredRange) {
      // Convert -1 to 1 scale to 0-100% position
      // Position = (value - (-1)) / (1 - (-1)) * 100 = (value + 1) / 2 * 100
      sliderPosition = ((attrValue + 1) / 2) * 100;
    } else {
      // Default to 0-5 scale
      sliderPosition = (attrValue / 5) * 100;
    }
    
    // Always render - data can be updated via spreadsheet import later
    return `
      <div class="flex flex-col gap-4 w-full mt-4">
        <p class="text-ui-title">
          ${qualityTopic}
        </p>
        <div class="flex flex-col gap-2 w-full relative">
          <!-- Slider Dot -->
          <div class="absolute w-1.5 h-1.5 bg-contrast rounded-full -top-0.75" style="left: calc(${sliderPosition}% - 3px)"></div>
          <!-- Slider Line -->
          <div class="h-0 w-full border-t border-primary"></div>
          <!-- Labels -->
          <div class="flex items-start justify-between w-full text-ui-small font-medium">
            <p>${minLabel}</p>
            <p>${maxLabel}</p>
          </div>
        </div>
      </div>
    `;
  }

  #showLoadingState() {
    const loading = document.getElementById('reviews-loading');
    if (loading) loading.style.display = 'flex';
  }

  #hideLoadingState() {
    const loading = document.getElementById('reviews-loading');
    if (loading) loading.style.display = 'none';
  }

  /**
   * Display error message to users and developers
   * @param {string} title - Error title
   * @param {string} message - Detailed error message
   * @private
   */
  #showError(title, message) {
    const reviewsList = document.getElementById('reviews-list');
    const reviewsSection = document.getElementById('reviews-list-section');
    
    if (!reviewsList) return;
    
    const errorHtml = `
      <div class="reviews-error-message" style="padding: 2rem; background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; margin: 1rem 0;">
        <h3 style="margin: 0 0 1rem 0; color: #856404; font-size: 1.25rem;">
          ⚠️ ${title}
        </h3>
        <p style="margin: 0 0 1rem 0; color: #856404; line-height: 1.6;">
          ${message.replace(/\n/g, '<br>')}
        </p>
        <details style="margin-top: 1rem;">
          <summary style="cursor: pointer; color: #856404; font-weight: 600;">
            Debugging Information
          </summary>
          <pre style="margin-top: 0.5rem; padding: 1rem; background: #fff; border: 1px solid #ddd; border-radius: 4px; font-size: 0.875rem; overflow-x: auto; color: #333;">Okendo User ID: ${this.#okendoUserId || '❌ Not Found'}

Checked Locations:
1. window.Shopify?.shop?.metafields?.okendo?.userId
2. window.okendo?.userId
3. document.querySelector('meta[name="okendo-user-id"]')?.content

Current Values:
- window.Shopify: ${typeof window.Shopify !== 'undefined' ? '✓ exists' : '❌ not found'}
- window.okendo: ${typeof window.okendo !== 'undefined' ? '✓ exists' : '❌ not found'}
- Okendo meta tag: ${document.querySelector('meta[name="okendo-user-id"]') ? '✓ exists' : '❌ not found'}</pre>
        </details>
      </div>
    `;
    
    reviewsList.innerHTML = errorHtml;
    if (reviewsSection) {
      reviewsSection.style.display = 'block';
    }
  }

  async #handleOrderByChange() {
    const orderBySelect = document.getElementById('order-by-select');
    this.#currentOrderBy = orderBySelect.value;
    this.#currentPage = 1;

    this.#renderedMediaCount = 0;
    this.#lastListFlushSignature = null;
    this.#lastGalleryFlushSignature = null;
    const galleryImages = document.getElementById('gallery-images');
    if (galleryImages) galleryImages.innerHTML = '';

    this.#showLoadingState();

    try {
      const [reviewsData, mediaData] = await Promise.all([
        this.#fetchReviews(25, this.#currentOrderBy),
        this.#fetchReviewsMedia()
      ]);
      this.#reviewsData.reviews = reviewsData;
      this.#reviewsData.media = mediaData;
      this.#allReviews = reviewsData?.reviews || [];
      this.#nextUrl = reviewsData?.nextUrl || null;
      this.#allMediaItems = this.#normalizeOkendoMediaList(mediaData?.media || []);
      this.#mediaNextUrl = mediaData?.nextUrl || null;
      this.#mergeReviewMediaIntoAllReviews();

      this.#renderReviews();
      await this.#populateGallery();
      this.#togglePhotoFilter();

      const existingModal = document.getElementById('store-reviews-gallery-modal');
      if (existingModal) {
        existingModal.remove();
        const existingTemplate = document.querySelector(`template[data-modal-content="store-reviews-gallery-modal"]`);
        if (existingTemplate) {
          existingTemplate.remove();
        }
        this.#renderedModalCardsCount = 0;
      }
      this.#createReviewsGalleryModal();
      this.#captureFlushSignaturesBaseline();

      if (this.#mediaNextUrl) {
        queueMicrotask(() => {
          this.#fetchAllMediaInBackground().catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('🔍 [OKENDO-STORE-REVIEWS] Background media fetch failed:', error);
            }
          });
        });
      }
      if (this.#nextUrl) {
        setTimeout(() => {
          this.#fetchAllReviewsInBackground().catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('🔍 [OKENDO-STORE-REVIEWS] Background fetch failed:', error);
            }
          });
        }, 100);
      }
    } catch (error) {
      console.error('🔍 [OKENDO-STORE-REVIEWS] Error changing order:', error);
    } finally {
      this.#hideLoadingState();
    }
  }

  async #handlePhotoFilterChange() {
    const hasPhotoFilter = document.getElementById('has-photo-filter');
    if (!hasPhotoFilter) return;

    this.#hasPhotoFilter = hasPhotoFilter.checked;
    this.#currentPage = 1;

    this.#lastListFlushSignature = null;
    this.#lastPaginationFilteredCount = -1;

    this.#mergeReviewMediaIntoAllReviews();
    await this.#renderReviews();
    this.#updatePagination();
    this.#captureFlushSignaturesBaseline();
    requestAnimationFrame(() => this.#updatePagination());

    if (this.#hasPhotoFilter) {
      this.#ensureBackgroundReviewsFetch();
    }

    // Update gallery visibility based on filter
    this.#togglePhotoFilter();
  }

  async #renderReviews() {
    const reviewsList = document.getElementById('reviews-list');
    const reviewsSection = document.getElementById('reviews-list-section');
    const reviewsControls = document.querySelector('.reviews-controls');
    if (!reviewsList || !reviewsSection) return;

    this.#syncHasPhotoFilterFromDom();

    // Calculate page indices
    const startIndex = (this.#currentPage - 1) * this.#itemsPerPage;
    const endIndex = startIndex + this.#itemsPerPage;
    
    let filteredReviews = this.#getFilteredReviewsForList();

    if (this.#hasPhotoFilter) {
      if (filteredReviews.length < endIndex) {
        this.#ensureBackgroundReviewsFetch();
      }
    }

    // Background fetch handles all fetching - don't call fetchMoreReviews here
    // If we need more reviews, they will be fetched by #fetchAllReviewsInBackground()
    // and #updateUIWithNewReviews() will update the UI when they arrive

    if (filteredReviews.length === 0) {
      if (this.#isAwaitingReviewsForCurrentPage(filteredReviews)) {
        this.#ensureBackgroundReviewsFetch();
        const loadingCopy =
          (window.reviewsStrings && window.reviewsStrings.loading) || 'Loading reviews...';
        reviewsList.innerHTML = `<p class="text-center">${loadingCopy}</p>`;
        reviewsSection.style.display = 'block';
        if (reviewsControls) {
          reviewsControls.style.display = this.#allReviews.length === 0 ? 'none' : 'flex';
        }
        this.#updatePagination();
        return;
      }

      const message = this.#hasPhotoFilter
        ? '<p>No reviews with photos available.</p>'
        : '<p>We want to know what you think. Be the first to leave a review.</p>';
      reviewsList.innerHTML = message;
      reviewsSection.style.display = 'block';
      
      // Hide controls completely when no reviews exist
      if (reviewsControls) {
        if (this.#allReviews.length === 0) {
          reviewsControls.style.display = 'none';
        } else {
          reviewsControls.style.display = 'flex';
        }
      }
      this.#updatePagination();
      return;
    }

    // Show controls when reviews exist
    if (reviewsControls) {
      reviewsControls.style.display = 'flex';
    }

    // Pagination - use updated indices after potential fetch
    const displayReviews = filteredReviews.slice(startIndex, endIndex);

    if (displayReviews.length === 0) {
      if (this.#isAwaitingReviewsForCurrentPage(filteredReviews)) {
        this.#ensureBackgroundReviewsFetch();
        const loadingCopy =
          (window.reviewsStrings && window.reviewsStrings.loading) || 'Loading reviews...';
        reviewsList.innerHTML = `<p class="text-center">${loadingCopy}</p>`;
        reviewsSection.style.display = 'block';
        this.#updatePagination();
        return;
      }

      if (this.#currentPage > 1) {
        this.#currentPage = Math.max(1, Math.ceil(filteredReviews.length / this.#itemsPerPage));
        return this.#renderReviews();
      }

      const message = this.#hasPhotoFilter
        ? '<p>No reviews with photos available.</p>'
        : '<p>We want to know what you think. Be the first to leave a review.</p>';
      reviewsList.innerHTML = message;
      reviewsSection.style.display = 'block';
      this.#updatePagination();
      return;
    }

    // Render reviews HTML following Figma design
    const reviewsHtml = displayReviews.map((review, index) => {
      const primaryMedia = review.primaryMedia || this.#getPrimaryMedia(review.media);
      const displayReview = this.#resolveListDisplayReview(review, primaryMedia);

      // Get reviewer name (uppercase first name only)
      const reviewerName = (displayReview.reviewer?.displayName || 'Anonymous').split(' ')[0].toUpperCase();
      
      // Format date as DD/MM/YYYY
      const reviewDate = this.#formatDateShort(displayReview.dateCreated);
      
      const reviewMedia = this.#getReviewListMediaItems(review);
      const hasMedia = reviewMedia.length > 0;
      const sliderId = `store-review-media-${index}-${String(this.#reviewRowMatchKey(review) || reviewerName).replace(/[^a-z0-9_-]/gi, '')}`;
      const mediaSlides = reviewMedia.map((item) => this.#buildReviewListMediaSlide(item)).join('');
      
      return `
      <div class="border-t border-secondary pt-6 md:pt-9 flex flex-col md:grid grid-cols-2 gap-y-(--gap-y-base) gap-x-(--gap-x-base) items-start w-full" data-rating="${displayReview.rating || 0}" data-date="${displayReview.dateCreated || ''}">
          <!-- Left Column: Name, Date, Stars, Title, Review -->
          <div class="lg:grid grid-cols-3 w-full shrink-0 flex-1 gap-y-(--gap-y-base) gap-x-(--gap-x-base)">
            <!-- Name & Date Column (product title and variant name on the left) -->
            <div class="flex flex-col gap-1 mb-4">
              <p class="text-ui-title flex justify-between">${reviewerName}<span class="text-ui-small opacity-70">${reviewDate}</span></p>
              ${this.#renderVariantLabelHtml(displayReview)}
              ${this.#renderVerifiedBuyerLabelHtml()}
            </div>
            
            <!-- Content Column -->
            <div class="flex flex-col gap-1 grow min-w-0 col-span-2">
              <!-- Stars -->
              <div class="text-ui-small">${this.#generateRatingStars(displayReview.rating || 0, 5)}</div>
              
              <!-- Review Text -->
              <p class="font-normal text-sm leading-[1.5] text-[#21201f] md:max-w-[384px]">${this.#formatListReviewBody(displayReview, primaryMedia)}</p>
              
              <!-- Price for Quality Bar (always render, even if data not available yet) -->
              ${this.#renderPriceForQualityBar(displayReview)}
              
              <!-- Other Attributes -->
              ${displayReview.attributesWithRating && displayReview.attributesWithRating.length > 0 ? `
                <div class="flex flex-col gap-2 mt-4">
                  ${displayReview.attributesWithRating
                    .filter(attr => {
                      // Exclude "Price for Quality" if it exists, as it's rendered separately above
                      // Check both name and title fields (Okendo API can use either)
                      const attrName = ((attr.name || attr.title || '')).toLowerCase();
                      return !((attrName.includes('price') && attrName.includes('quality')) ||
                               attrName === 'price for quality');
                    })
                    .map(attr => `
                    <div class="flex items-center gap-3">
                      <span class="text-xs font-medium text-[#21201f] shrink-0 max-w-[40%] break-words">${attr.title || attr.name || ''}</span>
                      <div class="flex items-center gap-2 grow">
                        <div class="h-1 bg-[#e0e0e0] rounded-full grow">
                          <div class="h-full bg-[#21201f] rounded-full" style="width: ${(attr.value / 5) * 100}%"></div>
                        </div>
                        <span class="text-xs text-[#21201f]">${attr.value}/5</span>
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
          
          <!-- Right Column: Media slider (arrows when more than one image/video) -->
          ${hasMedia ? `
            <div class="flex gap-2 w-full md:w-auto md:grow md:min-w-0 shrink-0 flex-1">
              <electric-slider
                data-id="${sliderId}"
                class="electric-slider group/snap relative w-full h-full"
                data-slider-step-by="page"
                data-drag="false"
              >
                ${reviewMedia.length > 1 ? this.#buildReviewMediaSliderNav(sliderId) : ''}
                <div
                  class="flex overflow-y-hidden overflow-x-scroll justify-start electric-slider__feed hide-scroll-bar snap-x snap-mandatory scroll-smooth [&_>_*]:w-column-basis [&_>_*]:shrink-0 [&_>_*]:snap-start gap-x-0 w-full h-full"
                  data-slider="${sliderId}"
                  tabindex="0"
                  style="--columns: 1; --md-columns: 1; --lg-columns: 1; --gap: 0px;"
                >
                  ${mediaSlides}
                </div>
              </electric-slider>
            </div>
          ` : ''}
      </div>
    `;
    }).join('');

    this.#paintHtmlReusingVideos(reviewsList, reviewsHtml);
    this.#hydrateVariantLabels(reviewsList);
    this.#syncReviewMediaSliderArrows(reviewsList);
    this.#prefetchReviewsForDisplayedList(displayReviews);
    
    this.#updatePagination();

    reviewsSection.style.display = 'block';
  }

  destroy() {
    if (this.#reviewsUiRefreshTimer) {
      clearTimeout(this.#reviewsUiRefreshTimer);
      this.#reviewsUiRefreshTimer = null;
    }
    if (this.#gallerySliderLayoutTimer) {
      clearTimeout(this.#gallerySliderLayoutTimer);
      this.#gallerySliderLayoutTimer = null;
    }
    this.#abortController.abort();
    this.#reviewDetailsById.clear();
    this.#reviewFetchInFlight.clear();
    this.#reviewFetchSettled.clear();
  }
}

// Icons needed for pagination
const icons = {
  circle_arrow_l: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="11" stroke="currentColor" stroke-width="2"/><path d="M14 8l-4 4 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  circle_arrow_r: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="11" stroke="currentColor" stroke-width="2"/><path d="M10 8l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const storeReviewsSection = document.querySelector('.store-reviews-section');
  if (storeReviewsSection) {
    new StoreReviewsSection();
  }
});

// Export for potential external use
window.StoreReviewsSection = StoreReviewsSection;
